import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from './Layout';
import './css/ChatboxPage.css';

// ─── STABLE MODULE-LEVEL AUDIO PLAYER ────────────────────────────────────────
// IMPORTANT: Defined OUTSIDE ChatboxPage so React never unmounts/remounts it
// when parent state changes (e.g. setIsMediaPlaying). Defining it inside
// ChatboxPage creates a new function reference on every parent render, which
// causes React to treat it as a completely different component, destroy the
// <audio> element, and stop playback mid-track.
const AudioPlayer = ({ audioUrl, audioFileName, mediaCaption, transcribedText, mediaPlayingRef, setIsMediaPlaying }) => {
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);
  const lastTimeRef = useRef(0);
  const shouldResumeRef = useRef(false);
  const isPlayingRef = useRef(false); // ref instead of state → no re-render cascade inside AudioPlayer

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTimeUpdate = () => { lastTimeRef.current = el.currentTime || 0; };

    const handlePlay = () => {
      mediaPlayingRef.current = true;
      setIsMediaPlaying(true);
      isPlayingRef.current = true;
      shouldResumeRef.current = true;
    };

    const handlePause = () => {
      mediaPlayingRef.current = false;
      setIsMediaPlaying(false);
      isPlayingRef.current = false;
      shouldResumeRef.current = false;
    };

    const handleEnded = () => {
      mediaPlayingRef.current = false;
      setIsMediaPlaying(false);
      isPlayingRef.current = false;
      shouldResumeRef.current = false;
      lastTimeRef.current = 0;
    };

    const handleLoadedMetadata = () => {
      if (lastTimeRef.current > 0) {
        el.currentTime = lastTimeRef.current;
        if (shouldResumeRef.current) {
          const p = el.play?.();
          if (p?.catch) p.catch(() => {});
        }
      }
    };

    const handleError = () => setAudioError(true);

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('loadedmetadata', handleLoadedMetadata);
    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    el.addEventListener('error', handleError);

    return () => {
      // Only clear the global playing flag if this instance was playing when unmounted
      if (isPlayingRef.current) {
        mediaPlayingRef.current = false;
        setIsMediaPlaying(false);
      }
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('loadedmetadata', handleLoadedMetadata);
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('error', handleError);
    };
  }, []); // ← Empty deps: listeners set up once on mount, never torn down mid-play

  if (!audioUrl) {
    return (
      <div className="audio-message">
        <span>🎵 Audio message (file not available)</span>
        {transcribedText && (
          <div className="transcription">
            <strong>Transcription:</strong> {transcribedText}
          </div>
        )}
        {mediaCaption && <p className="media-caption">{mediaCaption}</p>}
      </div>
    );
  }

  return (
    <div className="audio-message">
      {audioError ? (
        <div className="media-error">
          <span>🎵 Audio unavailable</span>
          {audioFileName && <p className="file-name">{audioFileName}</p>}
        </div>
      ) : (
        <div className="audio-player-container">
          <audio
            ref={audioRef}
            src={audioUrl}
            controls
            className="shared-audio"
            preload="metadata"
          >
            Your browser does not support the audio element.
          </audio>
          {audioFileName && <div className="audio-filename">🎵 {audioFileName}</div>}
        </div>
      )}
      {transcribedText && (
        <div className="transcription">
          <strong>Transcription:</strong> {transcribedText}
        </div>
      )}
      {mediaCaption && <p className="media-caption">{mediaCaption}</p>}
    </div>
  );
};

// ─── STABLE MODULE-LEVEL VIDEO PLAYER ────────────────────────────────────────
// Same reason as AudioPlayer — must live outside ChatboxPage.
const VideoPlayer = ({ videoUrl, videoFileName, mediaCaption, mediaPlayingRef, setIsMediaPlaying }) => {
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);
  const lastTimeRef = useRef(0);
  const shouldResumeRef = useRef(false);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const onTimeUpdate = () => { lastTimeRef.current = el.currentTime || 0; };

    const handlePlay = () => {
      mediaPlayingRef.current = true;
      setIsMediaPlaying(true);
      isPlayingRef.current = true;
      shouldResumeRef.current = true;
    };

    const handlePause = () => {
      mediaPlayingRef.current = false;
      setIsMediaPlaying(false);
      isPlayingRef.current = false;
      shouldResumeRef.current = false;
    };

    const handleEnded = () => {
      mediaPlayingRef.current = false;
      setIsMediaPlaying(false);
      isPlayingRef.current = false;
      shouldResumeRef.current = false;
      lastTimeRef.current = 0;
    };

    const handleLoadedMetadata = () => {
      if (lastTimeRef.current > 0) {
        el.currentTime = lastTimeRef.current;
        if (shouldResumeRef.current) {
          const p = el.play?.();
          if (p?.catch) p.catch(() => {});
        }
      }
    };

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('loadedmetadata', handleLoadedMetadata);
    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);

    return () => {
      if (isPlayingRef.current) {
        mediaPlayingRef.current = false;
        setIsMediaPlaying(false);
      }
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('loadedmetadata', handleLoadedMetadata);
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
    };
  }, []); // ← Empty deps: listeners set up once on mount

  if (!videoUrl) {
    return (
      <div className="video-message">
        <span>🎥 Video message (file not available)</span>
        {mediaCaption && <p className="media-caption">{mediaCaption}</p>}
      </div>
    );
  }

  return (
    <div className="video-message">
      {videoError ? (
        <div className="media-error">
          <span>🎥 Video unavailable</span>
          {videoFileName && <p className="file-name">{videoFileName}</p>}
        </div>
      ) : (
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          className="shared-video"
          preload="metadata"
          onError={() => setVideoError(true)}
        >
          Your browser does not support the video tag.
        </video>
      )}
      {mediaCaption && <p className="media-caption">{mediaCaption}</p>}
    </div>
  );
};

const ChatboxPage = () => {
  const [user, setUser] = useState(null);
  const [platform, setPlatform] = useState('whatsapp'); // 'facebook2', 'whatsapp', or 'facebook'
  const [whatsappSessions, setWhatsappSessions] = useState([]);
  const [facebookSessions, setFacebookSessions] = useState([]);
  const [facebook2Sessions, setFacebook2Sessions] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [activeTab, setActiveTab] = useState('aida');
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [shouldAutoScrollOnLoad, setShouldAutoScrollOnLoad] = useState(false);
  const [fbNameCache, setFbNameCache] = useState({});
  
  const whatsappPollingRef = useRef(null);
  const facebookPollingRef = useRef(null);
  const facebook2PollingRef = useRef(null);
  const platformRef = useRef(platform); // keeps polling closure in sync with current platform

  // Auto-refresh intervals
  const messagesPollingRef = useRef(null);
  const sessionsPollingRef = useRef(null);
  const [lastMessageId, setLastMessageId] = useState(null);
  
  const [isMediaPlaying, setIsMediaPlaying] = useState(false);
  const mediaPlayingRef = useRef(false);
  
  // Reply functionality states
  const [isRecording, setIsRecording] = useState(false);
  const [isPausedRecording, setIsPausedRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingTimerRef = useRef(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recorderError, setRecorderError] = useState(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const waveAnimationRef = useRef(null);
  const waveformCanvasRef = useRef(null);
  const discardingRef = useRef(false);
  const recordMimeRef = useRef('audio/mpeg');
  const recordExtRef = useRef('mp3');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replySuccess, setReplySuccess] = useState(null);
  const [replyError, setReplyError] = useState(null);
 
  const [resettingFlags, setResettingFlags] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(null);
  const [resetError, setResetError] = useState(null);
  
  // Handle session states
  const [handlingSession, setHandlingSession] = useState(false);
  const [handleSuccess, setHandleSuccess] = useState(null);
  const [handleError, setHandleError] = useState(null);
  
  // Revert session states
  const [revertingSession, setRevertingSession] = useState(false);
  const [revertSuccess, setRevertSuccess] = useState(null);
  const [revertError, setRevertError] = useState(null);

  // Appointment completed states
  const [markingCompleted, setMarkingCompleted] = useState(false);
  const [completedSuccess, setCompletedSuccess] = useState(null);
  const [completedError, setCompletedError] = useState(null);
  // Mark handled state (handleSuccess / handleError are already declared elsewhere and reused here)
  const [markingHandle, setMarkingHandle] = useState(false);
  
  const [voiceEligibility, setVoiceEligibility] = useState(null);
  const [checkingVoiceEligibility, setCheckingVoiceEligibility] = useState(false);

  const [facebookUserName, setFacebookUserName] = useState(null);
  const [loadingFacebookName, setLoadingFacebookName] = useState(false);
  
  // Media upload states
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Save / Edit Client Modal
  const [showSaveClientModal, setShowSaveClientModal] = useState(false);
  const [saveClientMode, setSaveClientMode] = useState('add'); // 'add' | 'edit'
  const [saveClientPkkey, setSaveClientPkkey] = useState(null);
  const [saveClientForm, setSaveClientForm] = useState({ name: '', ic: '', dob: '', phone: '', email: '' });
  const [savingClient, setSavingClient] = useState(false);
  const [loadingClientData, setLoadingClientData] = useState(false);
  const [saveClientError, setSaveClientError] = useState(null);
  const [saveClientSuccess, setSaveClientSuccess] = useState(null);
  
  // ── Add Appointment Modal States ──
  const [showAddApptModal, setShowAddApptModal] = useState(false);
  const [apptLockedClient, setApptLockedClient] = useState(null); // { id, name, ic }
  const [apptBranches, setApptBranches] = useState([]);
  const [apptDoctorsByBranch, setApptDoctorsByBranch] = useState([]);
  const [apptAvailableSlots, setApptAvailableSlots] = useState([]);
  const [apptBookedSlots, setApptBookedSlots] = useState([]);
  const [apptIsRestDay, setApptIsRestDay] = useState(false);
  const [apptWorkingHours, setApptWorkingHours] = useState('');
  const [apptTimeConflict, setApptTimeConflict] = useState('');
  const [apptLoading, setApptLoading] = useState(false);
  const [apptAlertMessage, setApptAlertMessage] = useState('');
  const [apptForm, setApptForm] = useState({
    title: '', description: '', start_time: '', duration_minutes: 30,
    client_id: '', doctor_id: '', branch_id: '', appointment_date: ''
  });

  // Add ref for messages container to enable auto-scroll
  const messagesListRef = useRef(null);
  
  // File input refs
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);


  // Keep platformRef in sync so polling closures always read the latest platform
  useEffect(() => {
    platformRef.current = platform;
  }, [platform]);
	// Platform Switch Handler
	const handlePlatformSwitch = (newPlatform) => {
	  if (newPlatform === platform) return;
	  
	  setPlatform(newPlatform);
	  
	  // Update chatSessions to show the selected platform's sessions
	  if (newPlatform === 'whatsapp') {
		setChatSessions(whatsappSessions);
	  } else if (newPlatform === 'facebook') {
		setChatSessions(facebookSessions);
	  } else if (newPlatform === 'facebook2') {
		setChatSessions(facebook2Sessions);
	  }
	  
	  setSelectedSession(null);
	  setMobileChatOpen(false);
	  setMessages([]);
	  setReplyMessage('');
	  clearMedia();
	  setReplyError(null);
	  setReplySuccess(null);
	  setShowMediaOptions(false);
	  setActiveTab('aida');
	  
	  // Stop message polling (session polling continues for both)
	  if (messagesPollingRef.current) clearInterval(messagesPollingRef.current);
	};

  const formatPhoneNumberEnhanced = (phoneNumber, clientName = null) => {
    if (!phoneNumber) return 'Unknown';
    
    let formattedPhone = phoneNumber.replace(/^\+/, '');
    
    if (formattedPhone.startsWith('60')) {
      const withoutCountryCode = formattedPhone.substring(2);
      
      if (withoutCountryCode.length >= 9) {
        let formatted;
        
        if (withoutCountryCode.length === 9) {
          formatted = `+60 ${withoutCountryCode.substring(0, 2)}-${withoutCountryCode.substring(2, 5)} ${withoutCountryCode.substring(5)}`;
        } else if (withoutCountryCode.length === 10) {
          formatted = `+60 ${withoutCountryCode.substring(0, 3)}-${withoutCountryCode.substring(3, 6)} ${withoutCountryCode.substring(6)}`;
        } else {
          formatted = `+60 ${withoutCountryCode}`;
        }
        
        if (clientName && clientName.trim()) {
          return `${formatted}`;
        }
        
        return formatted;
      }
    }
    
    if (clientName && clientName.trim()) {
      return `${clientName.trim()} (${formattedPhone})`;
    }
    
    return formattedPhone;
  };

  const formatPhoneNumber = formatPhoneNumberEnhanced;

  // Function to reset new message count for a session
  const resetNewMessageCount = async (sessionId) => {
    try {
      let endpoint;
      if (platform === 'whatsapp') {
        endpoint = `/api/chatboxContent/reset-new-message-count/${sessionId}`;
      } else if (platform === 'facebook') {
        endpoint = `/api/chatboxContent/facebook/reset-new-message-count/${sessionId}`;
      } else if (platform === 'facebook2') {
        endpoint = `/api/chatboxContent/facebook2/reset-new-message-count/${sessionId}`;
      }
        
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to reset new message count');
        return;
      }

      setChatSessions(prevSessions => 
        prevSessions.map(session => 
          session.session_id === sessionId 
            ? { ...session, new_message_count: 0 }
            : session
        )
      );
    } catch (error) {
      console.error('Error resetting new message count:', error);
    }
  };

	const renderSessionItem = (session) => {
	  // For WhatsApp keep phone formatting; for Facebook/2nd Facebook Page show the resolved name; 
	  let displayText;
	  if (platform === 'whatsapp') {
		displayText = formatPhoneNumber(session.phone_number, session.client_name);
	  } else if (platform === 'facebook') {
		// Prefer the name stored in DB (populated at webhook time), fall back to cache/loading
		displayText = session.user_name || fbNameCache[session.user_id] || 'Loading…';
	  } else if (platform === 'facebook2') {
		displayText = session.user_name || fbNameCache[session.user_id] || 'Loading…';
	  }

	  const isRegisteredClient = platform === 'whatsapp' && session.client_name && session.client_name.trim();
	  const hasHumanReply = session.human_reply === 1;
	  const hasNewMessages = session.new_message_count > 0;
	  const isAppointmentCompleted = session.appointment_completed === 1;
	  const isHandled = session.mark_handled === 1;

	  const latestMessageTime = session.latest_message_timestamp || session.last_activity;

	  return (
		<div
		  key={session.session_id}
		  className={`session-item ${selectedSession?.session_id === session.session_id ? 'active' : ''} ${hasHumanReply ? 'human-reply-session' : ''} ${isAppointmentCompleted ? 'appointment-completed-session' : ''} ${platform}`}
		  onClick={() => handleSessionSelect(session)}
		>
		  <div className="session-header">
			<span className="phone-number">
			  {displayText}
			</span>
			{isRegisteredClient && (
			  <span className="client-badge" title="Registered Client">
				👤 {session.client_name}
			  </span>
			)}
			{hasNewMessages && (
			  <span className="new-message-badge">
				{session.new_message_count}
			  </span>
			)}
		  </div>
		  <div className="session-meta">
			<div className="session-badges">
			  {isAppointmentCompleted && (
				<span className="completed-badge" title="Appointment Completed">
				  ✅ Completed
				</span>
			  )}
			  {isHandled && (
				<span className="handled-badge" title="Handled">
				  📌 Handled
				</span>
			  )}
			</div>
			<span className="last-activity">
			  {formatDate(latestMessageTime)}
			</span>
		  </div>
		</div>
	  );
	};

  // Auto-refresh messages function
  const startMessagesPolling = () => {
    if (messagesPollingRef.current) {
      clearInterval(messagesPollingRef.current);
    }

    // Guard: if a messages fetch is already in flight, skip that tick.
    let isMsgPollRunning = false;
    
    messagesPollingRef.current = setInterval(() => {
      if (isMsgPollRunning) return;
      if (!selectedSession?.session_id) return;
      isMsgPollRunning = true;
      fetchMessagesQuietly(selectedSession.session_id).finally(() => {
        isMsgPollRunning = false;
      });
    }, 3000);
  };

	const fetchMessagesQuietly = async (sessionId) => {
	  try {
		// Extra guard: if any <video>/<audio> is actively playing, skip update
		// (covers edge cases where mediaPlayingRef might not be set yet)
		const anyDomMediaPlaying = (() => {
		  const medias = document.querySelectorAll('video, audio');
		  for (const m of medias) {
			// HTMLMediaElement.paused === false means it's currently playing
			if (m && typeof m.paused === 'boolean' && !m.paused) return true;
		  }
		  return false;
		})();

		// CRITICAL: Don't update if media is currently playing
		if (mediaPlayingRef.current || anyDomMediaPlaying) {
		  console.log('Media playing → skip polling update');
		  return;
		}

		let endpoint;
		if (platform === 'whatsapp') {
		  endpoint = `/api/chatboxContent/chat-messages/${sessionId}`;
		} else if (platform === 'facebook') {
		  endpoint = `/api/chatboxContent/facebook/chat-messages/${sessionId}`;
		} else if (platform === 'facebook2') {
		  endpoint = `/api/chatboxContent/facebook2/chat-messages/${sessionId}`;
		}

		const response = await fetch(endpoint);
		if (!response.ok) return;

		const newMessages = await response.json();

		if (Array.isArray(newMessages) && newMessages.length > 0) {
		  // SMART UPDATE: Only update if there are actual new messages
		  setMessages(prevMessages => {
			// If no previous messages, just set the new ones
			if (!prevMessages || prevMessages.length === 0) {
			  setLastMessageId(newMessages[newMessages.length - 1]?.message_id);
			  return newMessages;
			}

			// Check if we have new messages by comparing the latest IDs
			const latestNewId = newMessages[newMessages.length - 1]?.message_id;
			const latestOldId = prevMessages[prevMessages.length - 1]?.message_id;

			if (latestNewId !== latestOldId) {
			  console.log('New messages detected, updating list');
			  setLastMessageId(latestNewId);

			  // Preserve existing objects (prevents remounting existing DOM)
			  const existingIds = new Set(prevMessages.map(m => m.message_id));
			  const genuinelyNewMessages = newMessages.filter(m => !existingIds.has(m.message_id));

			  if (genuinelyNewMessages.length > 0) {
				// ✅ FIX: proper array spread to append without touching old nodes
				return [...prevMessages, ...genuinelyNewMessages];
			  }

			  // If counts differ (e.g., deletions), fall back to full replace
			  if (newMessages.length !== prevMessages.length) {
				return newMessages;
			  }
			}

			// No changes → return previous to avoid re-render
			return prevMessages;
		  });
		}
	  } catch (err) {
		console.error('Error fetching messages quietly:', err);
	  }
	};

	// Auto-refresh sessions function
	const startSessionsPolling = () => {
	  // Stop existing polling
	  if (whatsappPollingRef.current) clearInterval(whatsappPollingRef.current);
	  if (facebookPollingRef.current) clearInterval(facebookPollingRef.current);
	  if (facebook2PollingRef.current) clearInterval(facebook2PollingRef.current);
	  
	  // Guard: prevent overlapping poll calls. If a poll is already in
	  // flight when the interval fires again, skip that tick entirely.
	  let isPollRunning = false;

	  const pollAll = async () => {
		if (isPollRunning) {
		  logger && console.log('[Polling] Skipping tick – previous poll still in flight');
		  return;
		}
		isPollRunning = true;
		try {
		  const { whatsappData, facebookData, facebook2Data } = await fetchAllPlatformsSessions();

		  // Use platformRef (not stale closure) to read the current platform
		  let freshSessions = [];
		  if (platformRef.current === 'whatsapp') {
			freshSessions = whatsappData;
		  } else if (platformRef.current === 'facebook') {
			freshSessions = facebookData;
		  } else if (platformRef.current === 'facebook2') {
			freshSessions = facebook2Data;
		  }

		  setChatSessions(freshSessions);

		  // Sync selectedSession so buttons/badges always reflect DB state.
		  setSelectedSession(prev => {
			if (!prev) return prev;
			const updated = freshSessions.find(s => s.session_id === prev.session_id);
			if (!updated) return prev;
			const hasChanged = Object.keys(updated).some(k => prev[k] !== updated[k]);
			return hasChanged ? { ...prev, ...updated } : prev;
		  });
		} catch (err) {
		  console.error('[Polling] pollAll error:', err);
		} finally {
		  isPollRunning = false;
		}
	  };
	  
	  // Start polling
	  whatsappPollingRef.current = setInterval(pollAll, 5000);
	};

  const fetchSessionsQuietly = async () => {
    try {
      let endpoint;
      let platformSetter; // also update the platform-specific state so the sync
                          // useEffect doesn't overwrite chatSessions with stale data
      if (platform === 'whatsapp') {
        endpoint = '/api/chatboxContent/chat-sessions';
        platformSetter = setWhatsappSessions;
      } else if (platform === 'facebook') {
        endpoint = '/api/chatboxContent/facebook/chat-sessions';
        platformSetter = setFacebookSessions;
      } else if (platform === 'facebook2') {
        endpoint = '/api/chatboxContent/facebook2/chat-sessions';
        platformSetter = setFacebook2Sessions;
      }
        
      const response = await fetch(endpoint);
      
      if (!response.ok) return;
      
      const sessions = await response.json();
      
      if (Array.isArray(sessions)) {
        const normalisedSessions = sessions.map(normaliseSession);

        // Update platform-specific state FIRST so the sync useEffect
        // [whatsappSessions, facebookSessions, facebook2Sessions, platform]
        // re-runs with fresh data rather than wiping chatSessions.
        if (platformSetter) platformSetter(normalisedSessions);

        setChatSessions(normalisedSessions);

        // Sync selectedSession with fresh DB data so buttons/badges update correctly.
        setSelectedSession(prev => {
          if (!prev) return prev;
          const updated = normalisedSessions.find(s => s.session_id === prev.session_id);
          if (!updated) return prev;
          const hasChanged = Object.keys(updated).some(k => prev[k] !== updated[k]);
          return hasChanged ? { ...prev, ...updated } : prev;
        });
      }
    } catch (err) {
      console.error('Error fetching sessions quietly:', err);
    }
  };
	
	const fetchFbName = async (psid, namePlatform = 'facebook') => {
	  if (!psid || fbNameCache[psid]) return;
	  try {
		const endpoint = namePlatform === 'facebook2'
		  ? `/api/chatboxContent/facebook2/user-info/${psid}`
		  : `/api/chatboxContent/facebook/user-info/${psid}`;
		const res = await fetch(endpoint);
		if (!res.ok) {
		  // Always cache a fallback so we don't keep retrying on HTTP errors
		  setFbNameCache(prev => ({ ...prev, [psid]: 'Facebook user' }));
		  return;
		}
		const data = await res.json();
		if (data?.success && data?.name) {
		  setFbNameCache(prev => ({ ...prev, [psid]: data.name }));
		} else {
		  // Fallback: keep empty so we don't spam requests
		  setFbNameCache(prev => ({ ...prev, [psid]: 'Facebook user' }));
		}
	  } catch {
		setFbNameCache(prev => ({ ...prev, [psid]: 'Facebook user' }));
	  }
	};

	// Given an array of sessions, queue missing lookups.
	// Sessions that already have user_name from the DB are skipped entirely.
	const ensureFacebookNames = async (sessions, namePlatform = 'facebook') => {
	  const missing = (sessions || [])
		.filter(s => s.user_id && !s.user_name)   // skip if DB already has the name
		.map(s => s.user_id)
		.filter(Boolean)
		.filter(psid => !fbNameCache[psid]);

	  // Limit concurrency a little to be nice to the API
	  const MAX_CONCURRENCY = 4;
	  let i = 0;
	  async function worker() {
		while (i < missing.length) {
		  const psid = missing[i++];
		  await fetchFbName(psid, namePlatform);
		}
	  }
	  await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENCY, missing.length) }, worker));
	};
	
	useEffect(() => {
	  if (platform === 'facebook' && Array.isArray(facebookSessions) && facebookSessions.length) {
		ensureFacebookNames(facebookSessions, 'facebook');
	  }
	  if (platform === 'facebook2' && Array.isArray(facebook2Sessions) && facebook2Sessions.length) {
		ensureFacebookNames(facebook2Sessions, 'facebook2');
	  }
	}, [platform, facebookSessions, facebook2Sessions]);

  // Start polling when platform or component mounts
	useEffect(() => {
	  startSessionsPolling();
	  
	  return () => {
		if (whatsappPollingRef.current) clearInterval(whatsappPollingRef.current);
		if (facebookPollingRef.current) clearInterval(facebookPollingRef.current);
		if (facebook2PollingRef.current) clearInterval(facebook2PollingRef.current);
	  };
	}, []);

  // Start/stop message polling based on selected session
  useEffect(() => {
    if (selectedSession) {
      startMessagesPolling();
    } else {
      if (messagesPollingRef.current) {
        clearInterval(messagesPollingRef.current);
      }
    }
    
    return () => {
      if (messagesPollingRef.current) {
        clearInterval(messagesPollingRef.current);
      }
    };
  }, [selectedSession, platform]);

	// Add after other useEffect hooks in ChatboxPage.js
	useEffect(() => {
	  const totalNew = getTotalNewMessages();
	  
	  const platformName = platform === 'whatsapp' ? 'WhatsApp' : platform === 'facebook' ? 'FB Ikigai ForHim' : 'FB Brandfolks ForHim';
	  
	  if (totalNew > 0) {
		document.title = `(${totalNew}) Chat Messages - ${platformName}`;
	  } else {
		document.title = `Chat Messages - ${platformName}`;
	  }
	  
	  return () => {
		document.title = 'Chat Messages';
	  };
	}, [chatSessions, platform]);

	useEffect(() => {
		const fetchFacebookUserName = async () => {
		  // Only fetch if platform is Facebook or 2nd Facebook Page and session is selected
		  if ((platform !== 'facebook' && platform !== 'facebook2') || !selectedSession?.user_id) {
			setFacebookUserName(null);
			return;
		  }

		  setLoadingFacebookName(true);
		  
		  try {
			const endpoint = platform === 'facebook2'
			  ? `/api/chatboxContent/facebook2/user-info/${selectedSession.user_id}`
			  : `/api/chatboxContent/facebook/user-info/${selectedSession.user_id}`;

			const response = await fetch(endpoint);
			
			if (!response.ok) {
			  console.error('Failed to fetch Facebook user name');
			  setFacebookUserName(null);
			  return;
			}

			const data = await response.json();
			
			if (data.success && data.name) {
			  setFacebookUserName(data.name);
			} else {
			  setFacebookUserName(null);
			}
		  } catch (error) {
			console.error('Error fetching Facebook user name:', error);
			setFacebookUserName(null);
		  } finally {
			setLoadingFacebookName(false);
		  }
		};

	fetchFacebookUserName();
  }, [platform, selectedSession?.user_id]);
	
  // Media handling functions
  const handleMediaSelect = (type) => {
    setShowMediaOptions(false);
    switch (type) {
      case 'image':
        imageInputRef.current?.click();
        break;
      case 'video':
        videoInputRef.current?.click();
        break;
      case 'document':
        documentInputRef.current?.click();
        break;
    }
  };

  const checkVoiceMessageEligibility = async (identifier) => {
    if (!identifier || platform !== 'whatsapp') return null;
    
    try {
      setCheckingVoiceEligibility(true);
      const response = await fetch(`/api/chatboxContent/check-voice-eligibility/${identifier}`);
      const result = await response.json();
      
      setVoiceEligibility(result);
      return result;
    } catch (error) {
      console.error('Voice eligibility check failed:', error);
      setVoiceEligibility({ 
        can_send_voice: false, 
        message: 'Failed to check voice message eligibility',
        error: error.message 
      });
      return null;
    } finally {
      setCheckingVoiceEligibility(false);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setReplyError('File size must be less than 10MB');
      setTimeout(() => setReplyError(null), 5000);
      return;
    }

    const validTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      video: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/webm'],
      document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
    };

    if (!validTypes[type].includes(file.type)) {
      setReplyError(`Invalid ${type} format. Please select a valid file.`);
      setTimeout(() => setReplyError(null), 5000);
      return;
    }

    setSelectedMedia(file);
    setMediaType(type);

    if (type === 'image' || type === 'video') {
      const reader = new FileReader();
      reader.onload = (e) => setMediaPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setMediaPreview(null);
    }

    setReplyError(null);
  };

  const clearMedia = () => {
    setSelectedMedia(null);
    setMediaPreview(null);
    setMediaType(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (documentInputRef.current) documentInputRef.current.value = '';
  };
	
	const getTotalNewMessages = (platformType) => {
	  let sessions;
	  if (platformType === 'whatsapp') {
		sessions = whatsappSessions;
	  } else if (platformType === 'facebook') {
		sessions = facebookSessions;
	  } else if (platformType === 'facebook2') {
		sessions = facebook2Sessions;
	  } else {
		sessions = chatSessions;
	  }
	  
	  if (!sessions || sessions.length === 0) return 0;
	  
	  return sessions.reduce((total, session) => {
		return total + (session.new_message_count || 0);
	  }, 0);
	};
	
	// Normalise numeric flags from DB (MySQL2 can return tinyint as string via COALESCE)
	const normaliseSession = (s) => ({
	  ...s,
	  appointment_completed: Number(s.appointment_completed ?? 0),
	  appointment_section:   Number(s.appointment_section   ?? 0),
	  appointment_session:   Number(s.appointment_session   ?? 0),
	  human_reply:           Number(s.human_reply           ?? 0),
	  human_section:         Number(s.human_section         ?? 0),
	  handle_session:        Number(s.handle_session        ?? 0),
	  mark_handled:          Number(s.mark_handled          ?? 0),
	});

	const fetchAllPlatformsSessions = async () => {
	  let whatsappData = [], facebookData = [], facebook2Data = [];

	  try {
		const whatsappResponse = await fetch('/api/chatboxContent/chat-sessions');
		if (whatsappResponse.ok) {
		  const data = await whatsappResponse.json();
		  whatsappData = Array.isArray(data) ? data.map(normaliseSession) : [];
		  setWhatsappSessions(whatsappData);
		}
	  } catch (err) {
		console.error('Error fetching WhatsApp sessions:', err);
	  }

	  try {
		const facebookResponse = await fetch('/api/chatboxContent/facebook/chat-sessions');
		if (facebookResponse.ok) {
		  const data = await facebookResponse.json();
		  facebookData = Array.isArray(data) ? data.map(normaliseSession) : [];
		  setFacebookSessions(facebookData);
		}
	  } catch (err) {
		console.error('Error fetching Facebook sessions:', err);
	  }

	  try {
		const facebook2Response = await fetch('/api/chatboxContent/facebook2/chat-sessions');
		if (facebook2Response.ok) {
		  const data = await facebook2Response.json();
		  facebook2Data = Array.isArray(data) ? data.map(normaliseSession) : [];
		  setFacebook2Sessions(facebook2Data);
		}
	  } catch (err) {
		console.error('Error fetching 2nd Facebook Page sessions:', err);
	  }

	  return { whatsappData, facebookData, facebook2Data };
	};

  const sendMediaMessage = async (overrideFile = null, overrideType = null, isVoice = false) => {
    const mediaFile = overrideFile || selectedMedia;
    const mType = overrideType || mediaType;
    if (!mediaFile || !selectedSession || uploadingMedia) return;

    try {
      setUploadingMedia(true);
      setReplyError(null);
      setReplySuccess(null);

      const formData = new FormData();
      formData.append('media', mediaFile);
      
      if (platform === 'whatsapp') {
        formData.append('phoneNumber', selectedSession.phone_number);
      } else if (platform === 'facebook') {
        formData.append('pageId', selectedSession.page_id);
        formData.append('userId', selectedSession.user_id);
      } else if (platform === 'facebook2') {
        formData.append('pageId', selectedSession.page_id);
        formData.append('userId', selectedSession.user_id);
      }
      
      formData.append('sessionId', selectedSession.session_id);
      formData.append('mediaType', mType);
      formData.append('adminUser', user?.usercode || 'Unknown');
      
      if (isVoice || mediaFile.name.includes('voice_')) {
        formData.append('isVoice', 'true');
      }
      
      if (replyMessage.trim()) {
        formData.append('caption', replyMessage.trim());
      }

      let endpoint;
      if (platform === 'whatsapp') {
        endpoint = '/api/chatboxContent/send-media';
      } else if (platform === 'facebook') {
        endpoint = '/api/chatboxContent/facebook/send-media';
      } else if (platform === 'facebook2') {
        endpoint = '/api/chatboxContent/facebook2/send-media';
      }
        
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      let result;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const textResult = await response.text();
        result = { error: 'Non-JSON response', details: textResult };
      }

      if (!response.ok) {
        throw new Error(result.details || result.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      setReplyMessage('');
      clearMedia();
      setReplySuccess(result.message || 'Media message sent successfully!');
      
      if (result.warning) {
        setReplySuccess(`${result.message} (${result.warning})`);
      }
      
      setTimeout(() => {
        setReplySuccess(null);
      }, 3000);

    } catch (err) {
      console.error('Media upload error:', err.message);
      
      let errorMessage = err.message;
      
      if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Cannot connect to server.';
      } else if (err.message.includes('500')) {
        errorMessage = 'Server error: ' + err.message;
      } else if (err.message.includes('413')) {
        errorMessage = 'File too large: Maximum 10MB allowed.';
      }
      
      setReplyError(errorMessage);
      
      setTimeout(() => {
        setReplyError(null);
      }, 10000);
    } finally {
      setUploadingMedia(false);
    }
  };

  const sendMessage = async () => {
    if (selectedMedia) {
      await sendMediaMessage();
    } else {
      await sendReplyMessage();
    }
  };

  // Media preview component
  const MediaPreview = () => {
    if (!selectedMedia) return null;

    return (
      <div className="media-preview">
        <div className="media-preview-header">
          <span className="media-preview-title">
            {mediaType === 'image' && '📷 Image'}
            {mediaType === 'video' && '🎥 Video'}
            {mediaType === 'document' && '📄 Document'}
            : {selectedMedia.name}
          </span>
          <button className="media-preview-close" onClick={clearMedia}>
            ✕
          </button>
        </div>
        
        {mediaPreview && (
          <div className="media-preview-content">
            {mediaType === 'image' && (
              <img src={mediaPreview} alt="Preview" className="media-preview-image" />
            )}
            {mediaType === 'video' && (
              <video src={mediaPreview} controls className="media-preview-video" />
            )}
          </div>
        )}
        
        {mediaType === 'document' && (
          <div className="media-preview-document">
            <div className="document-icon">📄</div>
            <div className="document-info">
              <div className="document-name">{selectedMedia.name}</div>
              <div className="document-size">{(selectedMedia.size / 1024).toFixed(1)} KB</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Media options dropdown
  const MediaOptions = () => {
    if (!showMediaOptions) return null;

    return (
      <>
        <div 
          className="media-options-backdrop" 
          onClick={() => setShowMediaOptions(false)}
        />
        <div className="media-options">
          <button className="media-option" onClick={() => handleMediaSelect('image')}>
            <span className="media-option-icon">📷</span>
            <span className="media-option-text">Photo</span>
          </button>
          <button className="media-option" onClick={() => handleMediaSelect('video')}>
            <span className="media-option-icon">🎥</span>
            <span className="media-option-text">Video</span>
          </button>
          <button className="media-option" onClick={() => handleMediaSelect('document')}>
            <span className="media-option-icon">📄</span>
            <span className="media-option-text">Document</span>
          </button>
        </div>
      </>
    );
  };
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
  }, []);

  useEffect(() => {
    if (messagesListRef.current && messages.length > 0) {
      if (shouldAutoScrollOnLoad) {
        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
        setShouldAutoScrollOnLoad(false);
      }
    }
  }, [messages, shouldAutoScrollOnLoad]);

  const getFilteredSessions = () => {
    if (!chatSessions || chatSessions.length === 0) return [];
    
    let filteredSessions = [];
    
    // WhatsApp, Facebook, and 2nd Facebook Page use appointment_section and human_section
      switch (activeTab) {
        case 'appointment':
          filteredSessions = chatSessions.filter(session => session.appointment_section === 1);
          break;
        case 'human':
          filteredSessions = chatSessions.filter(session => 
            session.human_section === 1 && session.appointment_section !== 1
          );
          break;
        case 'aida':
        default:
          filteredSessions = chatSessions.filter(session => 
            (session.appointment_section === 0 || session.appointment_section === null) && 
            (session.human_section === 0 || session.human_section === null)
          );
          break;
      }
    
    return filteredSessions.sort((a, b) => {
      const aHasNewMessages = (a.new_message_count || 0) > 0;
      const bHasNewMessages = (b.new_message_count || 0) > 0;
      
      if (aHasNewMessages && !bHasNewMessages) return -1;
      if (!aHasNewMessages && bHasNewMessages) return 1;
      
      const aTimestamp = new Date(a.latest_message_timestamp || a.last_activity || 0);
      const bTimestamp = new Date(b.latest_message_timestamp || b.last_activity || 0);
      
      return bTimestamp - aTimestamp;
    });
  };

  const getSessionCounts = () => {
    if (!chatSessions || chatSessions.length === 0) {
      return { aida: 0, appointment: 0, human: 0, handle: 0 };
    }
    
    // WhatsApp, Facebook, and 2nd Facebook Page use appointment_section and human_section
      const appointment = chatSessions.filter(session => session.appointment_section === 1).length;
      const human = chatSessions.filter(session => 
        session.human_section === 1 && session.appointment_section !== 1
      ).length;
      const aida = chatSessions.filter(session => 
        (session.appointment_section === 0 || session.appointment_section === null) && 
        (session.human_section === 0 || session.human_section === null)
      ).length;
      
      return { aida, appointment, human, handle: 0 };
  };
  
  const resetSessionFlags = async () => {
    if (!selectedSession || resettingFlags) return;

    let identifier;
    if (platform === 'whatsapp') {
      identifier = formatPhoneNumber(selectedSession.phone_number, selectedSession.client_name);
    } else if (platform === 'facebook') {
      identifier = formatPhoneNumber(selectedSession.user_id, selectedSession.client_name);
    } else if (platform === 'facebook2') {
      identifier = selectedSession.user_id;
    }
      
    const confirmed = window.confirm(
      `Are you sure you want to switch this conversation back to AI Reply?\n\nIdentifier: ${identifier}`
    );
    
    if (!confirmed) return;

    try {
      setResettingFlags(true);
      setResetError(null);
      setResetSuccess(null);

      let endpoint;
      if (platform === 'whatsapp') {
        endpoint = `/api/chatboxContent/reset-session-flags/${selectedSession.session_id}`;
      } else if (platform === 'facebook') {
        endpoint = `/api/chatboxContent/facebook/reset-session-flags/${selectedSession.session_id}`;
      } else if (platform === 'facebook2') {
        endpoint = `/api/chatboxContent/facebook2/reset-session-flags/${selectedSession.session_id}`;
      }
        
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.details || result.error || 'Failed to reset session flags');
      }

      setResetSuccess(result.message || 'Session flags reset successfully!');
      
      setTimeout(() => {
        setResetSuccess(null);
      }, 3000);

      fetchSessionsQuietly();

    } catch (err) {
      console.error('Error resetting session flags:', err);
      setResetError(err.message);
      
      setTimeout(() => {
        setResetError(null);
      }, 5000);
    } finally {
      setResettingFlags(false);
    }
  };

  // Handle Session / Revert Session not applicable for 2nd Facebook Page platform

  // Toggle appointment completed (WhatsApp & Website appointment sessions)
  const toggleAppointmentCompleted = async (targetValue) => {
    if (!selectedSession || markingCompleted) return;

    try {
      setMarkingCompleted(true);
      setCompletedError(null);
      setCompletedSuccess(null);

      let endpoint;
      if (platform === 'whatsapp') {
        endpoint = `/api/chatboxContent/appointment-completed/${selectedSession.session_id}`;
      } else if (platform === 'facebook2') {
        endpoint = `/api/chatboxContent/facebook2/appointment-completed/${selectedSession.session_id}`;
      } else {
        throw new Error('Mark Completed not supported for this platform');
      }

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: targetValue, markedBy: user?.usercode || 'Unknown' }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.details || result.error || 'Failed to update appointment status');
      }

      setCompletedSuccess(result.message || 'Appointment status updated!');

      // Update the selected session locally (merge in tracking fields returned by the API)
      setSelectedSession(prev => ({
        ...prev,
        appointment_completed: targetValue,
        appointment_completed_first_at: result.appointment_completed_first_at ?? prev.appointment_completed_first_at,
        appointment_completed_first_by: result.appointment_completed_first_by ?? prev.appointment_completed_first_by,
        appointment_completed_first_by_name: result.appointment_completed_first_by_name ?? prev.appointment_completed_first_by_name,
        appointment_completed_last_at: result.appointment_completed_last_at ?? prev.appointment_completed_last_at,
        appointment_completed_last_by: result.appointment_completed_last_by ?? prev.appointment_completed_last_by,
        appointment_completed_last_by_name: result.appointment_completed_last_by_name ?? prev.appointment_completed_last_by_name
      }));

      // Also update in the sessions list
      setChatSessions(prevSessions =>
        prevSessions.map(session =>
          session.session_id === selectedSession.session_id
            ? {
                ...session,
                appointment_completed: targetValue,
                appointment_completed_first_at: result.appointment_completed_first_at ?? session.appointment_completed_first_at,
                appointment_completed_first_by: result.appointment_completed_first_by ?? session.appointment_completed_first_by,
                appointment_completed_first_by_name: result.appointment_completed_first_by_name ?? session.appointment_completed_first_by_name,
                appointment_completed_last_at: result.appointment_completed_last_at ?? session.appointment_completed_last_at,
                appointment_completed_last_by: result.appointment_completed_last_by ?? session.appointment_completed_last_by,
                appointment_completed_last_by_name: result.appointment_completed_last_by_name ?? session.appointment_completed_last_by_name
              }
            : session
        )
      );

      setTimeout(() => {
        setCompletedSuccess(null);
      }, 3000);

      fetchSessionsQuietly();

    } catch (err) {
      console.error('Error toggling appointment completed:', err);
      setCompletedError(err.message);

      setTimeout(() => {
        setCompletedError(null);
      }, 5000);
    } finally {
      setMarkingCompleted(false);
    }
  };

  // ── Chat using other WhatsApp ──
  const handleOpenWhatsApp = () => {
    if (!user) { alert('You must be logged in to use this feature.'); return; }
    if (!selectedSession?.phone_number) return;
    const raw = selectedSession.phone_number.replace(/[\s\-()+]/g, '');
    window.open(`https://wa.me/${raw}`, '_blank', 'noopener,noreferrer');
  };

  // ── Save / Edit Client Modal ──
  const handleSaveClientOpen = async () => {
    if (!user) { alert('You must be logged in to use this feature.'); return; }
    setSaveClientError(null);
    setSaveClientSuccess(null);

    const isRegistered = selectedSession?.client_name && selectedSession.client_name.trim();

    if (isRegistered) {
      // Edit mode — fetch full client data first
      setSaveClientMode('edit');
      setSaveClientForm({ name: '', ic: '', dob: '', phone: '', email: '' });
      setSaveClientPkkey(null);
      setShowSaveClientModal(true);
      setLoadingClientData(true);
      try {
        const res = await fetch(`/api/chatboxContent/client-by-phone/${encodeURIComponent(selectedSession.phone_number)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load client data.');
        setSaveClientPkkey(data.client.pkkey);
        setSaveClientForm({
          name: data.client.name || '',
          ic: data.client.ic || '',
          dob: data.client.dob || '',
          phone: data.client.phoneNumber || '',
          email: data.client.email || '',
        });
      } catch (err) {
        setSaveClientError(err.message);
      } finally {
        setLoadingClientData(false);
      }
    } else {
      // Add mode
      setSaveClientMode('add');
      setSaveClientPkkey(null);
      setSaveClientForm({
        name: '',
        ic: '',
        dob: '',
        phone: selectedSession?.phone_number || '',
        email: '',
      });
      setShowSaveClientModal(true);
    }
  };

  const handleSaveClientSubmit = async () => {
    if (!saveClientForm.name.trim()) { setSaveClientError('Name is required.'); return; }
    if (!saveClientForm.phone.trim()) { setSaveClientError('Phone number is required.'); return; }
    try {
      setSavingClient(true);
      setSaveClientError(null);

      const payload = {
        name: saveClientForm.name.trim(),
        ic: saveClientForm.ic.trim() || null,
        dob: saveClientForm.dob || null,
        phoneNumber: saveClientForm.phone.trim(),
        email: saveClientForm.email.trim() || null,
      };

      let response;
      if (saveClientMode === 'edit' && saveClientPkkey) {
        response = await fetch(`/api/chatboxContent/edit-client/${saveClientPkkey}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch('/api/chatboxContent/save-client', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Operation failed.');
      setSaveClientSuccess(saveClientMode === 'edit' ? 'Client updated successfully!' : 'Client saved successfully!');
      setTimeout(() => setShowSaveClientModal(false), 1200);
    } catch (err) {
      setSaveClientError(err.message);
    } finally {
      setSavingClient(false);
    }
  };

  // ── Add Appointment — helpers ──
  const apptFormatDateToString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const apptFormatTimeDisplay = (t) => {
    if (!t) return '';
    if (typeof t === 'string' && t.length >= 5) return t.substring(0, 5);
    return t;
  };

  const apptResetModal = () => {
    setApptForm({ title: '', description: '', start_time: '', duration_minutes: 30, client_id: '', doctor_id: '', branch_id: '', appointment_date: '' });
    setApptDoctorsByBranch([]);
    setApptAvailableSlots([]);
    setApptBookedSlots([]);
    setApptIsRestDay(false);
    setApptWorkingHours('');
    setApptTimeConflict('');
    setApptAlertMessage('');
    setApptLockedClient(null);
  };

  const apptLoadBranches = async () => {
    try {
      const res = await fetch('/api/calendarContent/branches');
      const data = await res.json();
      if (data.success) setApptBranches(data.data);
    } catch (e) { console.error('appt: load branches', e); }
  };

  const apptLoadDoctorsByBranch = async (branchId) => {
    if (!branchId) { setApptDoctorsByBranch([]); return; }
    try {
      const res = await fetch(`/api/calendarContent/doctors-by-branch/${branchId}`);
      const data = await res.json();
      if (data.success) setApptDoctorsByBranch(data.data);
    } catch (e) { console.error('appt: load doctors', e); }
  };

  const apptLoadAvailableSlots = async (dateString, doctorId) => {
    if (!doctorId || !dateString) {
      setApptAvailableSlots([]); setApptBookedSlots([]); setApptIsRestDay(false); setApptWorkingHours(''); setApptTimeConflict('');
      return;
    }
    try {
      const res = await fetch(`/api/calendarContent/available-slots/${doctorId}/${dateString}`);
      const data = await res.json();
      if (data.success) {
        setApptAvailableSlots(data.data.availableSlots);
        setApptBookedSlots(data.data.bookedSlots || []);
        setApptIsRestDay(data.isRestDay || false);
        setApptWorkingHours(data.data.workingHours || '');
        setApptTimeConflict('');
        if (data.isRestDay) setApptAlertMessage(data.message);
      } else {
        setApptAvailableSlots([]); setApptBookedSlots([]);
      }
    } catch (e) {
      setApptAvailableSlots([]); setApptBookedSlots([]);
    }
  };

  const apptValidateTimeSlot = (startTime, durationMinutes, currentBookedSlots, currentIsRestDay) => {
    if (!startTime) { setApptTimeConflict(''); return; }
    if (currentIsRestDay) { setApptTimeConflict('Consultant is on rest day. Please select another date.'); return; }
    const [sh, sm] = startTime.split(':').map(Number);
    const endTotalMin = sh * 60 + sm + parseInt(durationMinutes || 30);
    const endH = String(Math.floor(endTotalMin / 60)).padStart(2, '0');
    const endM = String(endTotalMin % 60).padStart(2, '0');
    const endTime = `${endH}:${endM}`;
    const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const newStart = toMin(startTime);
    const newEnd = toMin(endTime);
    const conflict = currentBookedSlots.find(b => {
      const bStart = toMin(b.start_time);
      const bEnd = toMin(b.end_time);
      return newStart < bEnd && newEnd > bStart;
    });
    if (conflict) {
      setApptTimeConflict(`⛔ Time conflict: Consultant already has an appointment from ${conflict.start_time} to ${conflict.end_time}. Please choose a different time or duration.`);
    } else {
      setApptTimeConflict('');
    }
  };

  const apptHandleBranchChange = (branchId) => {
    setApptForm(prev => ({ ...prev, branch_id: branchId, doctor_id: '', start_time: '' }));
    setApptAvailableSlots([]); setApptIsRestDay(false); setApptWorkingHours('');
    apptLoadDoctorsByBranch(branchId);
  };

  const apptHandleDoctorChange = (doctorId) => {
    setApptForm(prev => ({ ...prev, doctor_id: doctorId, start_time: '' }));
    setApptAvailableSlots([]); setApptIsRestDay(false); setApptWorkingHours('');
    if (doctorId && apptForm.appointment_date) {
      apptLoadAvailableSlots(apptForm.appointment_date, doctorId);
    }
  };

  const apptHandleDateChange = (dateString) => {
    setApptForm(prev => ({ ...prev, appointment_date: dateString, start_time: '' }));
    setApptAvailableSlots([]); setApptIsRestDay(false); setApptWorkingHours('');
    if (apptForm.doctor_id && dateString) {
      apptLoadAvailableSlots(dateString, apptForm.doctor_id);
    }
  };

  const handleSaveAppt = async () => {
    if (!apptForm.title.trim()) { setApptAlertMessage('Please enter an appointment title'); return; }
    if (!apptForm.client_id) { setApptAlertMessage('Client is missing. Please close and reopen.'); return; }
    if (!apptForm.branch_id) { setApptAlertMessage('Please select a branch'); return; }
    if (!apptForm.doctor_id) { setApptAlertMessage('Please select a consultant'); return; }
    if (!apptForm.start_time) { setApptAlertMessage('Please select a start time'); return; }
    if (apptIsRestDay) { setApptAlertMessage('The consultant is on rest day. Please select another date or consultant.'); return; }
    if (apptTimeConflict) { setApptAlertMessage(apptTimeConflict); return; }
    try {
      setApptLoading(true);
      const res = await fetch('/api/calendarContent/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...apptForm, user_id: user.pkkey })
      });
      const data = await res.json();
      if (data.success) {
        setShowAddApptModal(false);
        apptResetModal();
      } else {
        setApptAlertMessage(data.message || 'Failed to create appointment');
      }
    } catch (e) {
      setApptAlertMessage('Failed to create appointment');
    } finally {
      setApptLoading(false);
    }
  };

  // ── Add Appointment guard ──
  const handleAddAppointmentClick = async () => {
    const isClient = selectedSession?.client_name && selectedSession.client_name.trim();
    if (!isClient) {
      alert('This contact has not been saved as a client yet. Please save the client first before adding an appointment.');
      return;
    }
    apptResetModal();
    // Fetch client pkkey via phone
    try {
      const res = await fetch(`/api/chatboxContent/client-by-phone/${encodeURIComponent(selectedSession.phone_number)}`);
      const data = await res.json();
      if (!res.ok || !data.client) {
        alert('Could not load client info. Please try again.');
        return;
      }
      const lockedClient = { id: data.client.pkkey, name: data.client.name, ic: data.client.ic || '' };
      setApptLockedClient(lockedClient);
      setApptForm(prev => ({
        ...prev,
        client_id: data.client.pkkey,
        appointment_date: apptFormatDateToString(new Date())
      }));
    } catch (e) {
      alert('Could not load client info. Please try again.');
      return;
    }
    // Load branches if not yet loaded
    if (apptBranches.length === 0) await apptLoadBranches();
    setShowAddApptModal(true);
  };

  const toggleHandle = async (targetValue) => {
    if (!selectedSession || markingHandle) return;

    try {
      setMarkingHandle(true);
      setHandleError(null);
      setHandleSuccess(null);

      if (platform !== 'whatsapp') {
        throw new Error('Mark Handle not supported for this platform');
      }
      const endpoint = `/api/chatboxContent/mark-handled/${selectedSession.session_id}`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handled: targetValue, markedBy: user?.usercode || 'Unknown' }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.details || result.error || 'Failed to update handled status');
      }

      setHandleSuccess(result.message || 'Handled status updated!');

      // Update the selected session locally (merge in tracking fields returned by the API)
      setSelectedSession(prev => ({
        ...prev,
        mark_handled: targetValue,
        mark_handled_first_at: result.mark_handled_first_at ?? prev.mark_handled_first_at,
        mark_handled_first_by: result.mark_handled_first_by ?? prev.mark_handled_first_by,
        mark_handled_first_by_name: result.mark_handled_first_by_name ?? prev.mark_handled_first_by_name,
        mark_handled_last_at: result.mark_handled_last_at ?? prev.mark_handled_last_at,
        mark_handled_last_by: result.mark_handled_last_by ?? prev.mark_handled_last_by,
        mark_handled_last_by_name: result.mark_handled_last_by_name ?? prev.mark_handled_last_by_name
      }));

      // Also update in the sessions list
      setChatSessions(prevSessions =>
        prevSessions.map(session =>
          session.session_id === selectedSession.session_id
            ? {
                ...session,
                mark_handled: targetValue,
                mark_handled_first_at: result.mark_handled_first_at ?? session.mark_handled_first_at,
                mark_handled_first_by: result.mark_handled_first_by ?? session.mark_handled_first_by,
                mark_handled_first_by_name: result.mark_handled_first_by_name ?? session.mark_handled_first_by_name,
                mark_handled_last_at: result.mark_handled_last_at ?? session.mark_handled_last_at,
                mark_handled_last_by: result.mark_handled_last_by ?? session.mark_handled_last_by,
                mark_handled_last_by_name: result.mark_handled_last_by_name ?? session.mark_handled_last_by_name
              }
            : session
        )
      );

      setTimeout(() => {
        setHandleSuccess(null);
      }, 3000);

      fetchSessionsQuietly();

    } catch (err) {
      console.error('Error toggling handled:', err);
      setHandleError(err.message);

      setTimeout(() => {
        setHandleError(null);
      }, 5000);
    } finally {
      setMarkingHandle(false);
    }
  };

  // Initial fetch of chat sessions
	useEffect(() => {
	  const fetchInitialSessions = async () => {
		try {
		  setLoading(true);
		  setError(null);
		  
		  // fetchAllPlatformsSessions internally calls setWhatsappSessions /
		  // setFacebookSessions / setFacebook2Sessions. The sync useEffect
		  // [whatsappSessions, facebookSessions, facebook2Sessions, platform]
		  // already reacts to those changes and updates chatSessions correctly.
		  // Do NOT duplicate that work here — the local closure variables
		  // (whatsappSessions etc.) are still [] at this point and would
		  // overwrite the freshly-fetched data with empty arrays.
		  await fetchAllPlatformsSessions();
		  
		} catch (err) {
		  console.error('Error fetching initial sessions:', err);
		  setError(err.message);
		} finally {
		  setLoading(false);
		}
	  };

	  fetchInitialSessions();
	}, []); // ← Only run once on mount, no dependency on [platform]
	
	useEffect(() => {
	  if (platform === 'whatsapp') {
		setChatSessions(whatsappSessions);
	  } else if (platform === 'facebook') {
		setChatSessions(facebookSessions);
	  } else if (platform === 'facebook2') {
		setChatSessions(facebook2Sessions);
	  }
	}, [whatsappSessions, facebookSessions, facebook2Sessions, platform]);

  const fetchMessages = async (sessionId) => {
    try {
      setLoadingMessages(true);
      
      let endpoint;
      if (platform === 'whatsapp') {
        endpoint = `/api/chatboxContent/chat-messages/${sessionId}`;
      } else if (platform === 'facebook') {
        endpoint = `/api/chatboxContent/facebook/chat-messages/${sessionId}`;
      } else if (platform === 'facebook2') {
        endpoint = `/api/chatboxContent/facebook2/chat-messages/${sessionId}`;
      }
        
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          errorData = { error: 'Unknown error', details: errorText };
        }
        
        throw new Error(`HTTP ${response.status}: ${errorData.error || 'Unknown error'}`);
      }
      
      const messages = await response.json();
      setMessages(Array.isArray(messages) ? messages : []);
      
      if (messages.length > 0) {
        setLastMessageId(messages[messages.length - 1].message_id);
      }
      
    } catch (err) {
      console.error('Error fetching messages:', err);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendReplyMessage = async () => {
    if (!replyMessage.trim() || !selectedSession || sendingReply) return;

    try {
      setSendingReply(true);
      setReplyError(null);
      setReplySuccess(null);

      const payload = {
        message: replyMessage.trim(),
        sessionId: selectedSession.session_id,
        adminUser: user?.usercode || 'Unknown'
      };
      
      if (platform === 'whatsapp') {
        payload.phoneNumber = selectedSession.phone_number;
      } else if (platform === 'facebook') {
        payload.pageId = selectedSession.page_id;
        payload.userId = selectedSession.user_id;
      } else if (platform === 'facebook2') {
        payload.pageId = selectedSession.page_id;
        payload.userId = selectedSession.user_id;
      }
      
      let endpoint;
      if (platform === 'whatsapp') {
        endpoint = '/api/chatboxContent/send-reply';
      } else if (platform === 'facebook') {
        endpoint = '/api/chatboxContent/facebook/send-reply';
      } else if (platform === 'facebook2') {
        endpoint = '/api/chatboxContent/facebook2/send-reply';
      }
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.details || result.error || 'Failed to send message');
      }

      setReplyMessage('');
      setReplySuccess(result.message || 'Message sent successfully!');
      
      if (result.warning) {
        setReplySuccess(`${result.message} (${result.warning})`);
      }
      
      setTimeout(() => {
        setReplySuccess(null);
      }, 3000);

    } catch (err) {
      console.error('Error sending reply:', err);
      setReplyError(err.message);
      
      setTimeout(() => {
        setReplyError(null);
      }, 5000);
    } finally {
      setSendingReply(false);
    }
  };

  const handleReplyKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReplyInputChange = (e) => {
    setReplyMessage(e.target.value);
    if (showMediaOptions) {
      setShowMediaOptions(false);
    }
  };

  // Recorder helpers (only for WhatsApp)
  const fmtTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${m}:${ss}`;
  };

  const drawWave = () => {
    const canvas = waveformCanvasRef.current;
    if (!canvas || !analyserRef.current) return;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    if (!dataArrayRef.current) dataArrayRef.current = new Uint8Array(bufferLength);
    const draw = () => {
      if (!analyserRef.current) return;
      analyser.getByteTimeDomainData(dataArrayRef.current);
      const width = canvas.width = canvas.clientWidth;
      const height = canvas.height = canvas.clientHeight;
      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 2;
      ctx.beginPath();
      const slice = width / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArrayRef.current[i] / 128.0;
        const y = v * height / 2;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        x += slice;
      }
      ctx.stroke();
      waveAnimationRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  const stopWave = () => {
    if (waveAnimationRef.current) cancelAnimationFrame(waveAnimationRef.current);
    waveAnimationRef.current = null;
  };

  const clearRecorderState = () => {
    setIsRecording(false);
    setIsPausedRecording(false);
    setRecordingSeconds(0);
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = null;
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    try { if (audioContextRef.current) audioContextRef.current.close(); } catch {}
    audioContextRef.current = null;
    analyserRef.current = null;
    stopWave();
    try { const mr = mediaRecorderRef.current; if (mr && mr.stream) mr.stream.getTracks().forEach(t => t.stop()); } catch {}
    mediaRecorderRef.current = null;
  };
  
  const startRecording = async () => {
    if (platform !== 'whatsapp') {
      setReplyError('Voice messages are only available for WhatsApp');
      setTimeout(() => setReplyError(null), 3000);
      return;
    }
    
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setReplyError('Microphone not available in this browser.');
      return;
    }
    
    try {
      setRecorderError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      let options = {};
      
      if (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')) {
        options.mimeType = 'audio/ogg; codecs=opus';
        recordMimeRef.current = 'audio/ogg';
        recordExtRef.current = 'ogg';
      } else if (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
        options.mimeType = 'audio/webm; codecs=opus';
        recordMimeRef.current = 'audio/ogg';
        recordExtRef.current = 'ogg';
      } else {
        options.mimeType = 'audio/mpeg';
        recordMimeRef.current = 'audio/mpeg';
        recordExtRef.current = 'mp3';
      }
      
      const mr = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current = mr;
      recordedChunksRef.current = [];
      
      mr.ondataavailable = e => { 
        if (e.data && e.data.size > 0) {
          recordedChunksRef.current.push(e.data); 
        }
      };
      
      mr.onstop = () => {
        if (discardingRef.current) {
          discardingRef.current = false;
          recordedChunksRef.current = [];
          return;
        }
        
        const blob = new Blob(recordedChunksRef.current, { 
          type: recordMimeRef.current 
        });
        const url = URL.createObjectURL(blob);
        
        setRecordedBlob(blob);
        setRecordedUrl(url);
      };
      
      mr.start();
      setIsRecording(true);
      
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContextRef.current.createMediaStreamSource(stream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        source.connect(analyserRef.current);
        drawWave();
      } catch (audioContextError) {
        console.warn('Audio visualization setup failed:', audioContextError);
      }
      
      recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
      
    } catch (e) {
      console.error('Recording setup error:', e);
      setRecorderError(e.message || 'Failed to start recording.');
    }
  };

  const pauseRecording = async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
        setIsPausedRecording(true);
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        if (audioContextRef.current?.state === 'running') await audioContextRef.current.suspend();
      }
    } catch {}
  };

  const resumeRecording = async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
        setIsPausedRecording(false);
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
        if (audioContextRef.current?.state === 'suspended') await audioContextRef.current.resume();
      }
    } catch {}
  };

  const stopRecording = () => {
    discardingRef.current = false;
    try {
      const mr = mediaRecorderRef.current;
      if (mr && (mr.state === 'recording' || mr.state === 'paused')) {
        mr.stop();
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      stopWave();
      setIsRecording(false);
    } catch {}
  };

  const cancelRecording = () => {
    try {
      const mr = mediaRecorderRef.current;
      if (mr && (mr.state === 'recording' || mr.state === 'paused')) {
        discardingRef.current = true;
        recordedChunksRef.current = [];
        mr.stop();
      }
    } catch {}
    clearRecorderState();
  };
  
  const sendRecordedAudio = async () => {
    if (!recordedBlob) return;
    try {
      const file = new File(
        [recordedBlob], 
        `voice_${Date.now()}.ogg`,
        { type: 'audio/ogg' }
      );
      
      setSelectedMedia(file);
      setMediaType('audio');
      clearRecorderState();
      await sendMediaMessage(file, 'audio', true);
    } catch (e) {
      setReplyError(e.message || 'Failed to send voice message.');
      setTimeout(() => setReplyError(null), 5000);
    }
  };

  // Icon Components
  const SendIcon = () => (
    <svg 
      className="send-icon" 
      viewBox="0 0 24 24" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
    </svg>
  );

  const MicIcon = () => (
    <svg className="mic-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 15a3 3 0 0 0 3-3V9a3 3 0 0 0-6 0v3a3 3 0 0 0 3 3z" />
        <path d="M19 12a7 7 0 0 1-14 0" />
        <line x1="12" y1="19" x2="12" y2="21.5" />
        <line x1="9" y1="21.5" x2="15" y2="21.5" />
      </g>
    </svg>
  );

  const PauseIcon = () => (<svg className="rec-icon" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>);
  const ResumeIcon = () => (<svg className="rec-icon" viewBox="0 0 24 24"><polygon points="8,5 19,12 8,19"/></svg>);
  const StopIcon = () => (<svg className="rec-icon" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12"/></svg>);
  const TrashIcon = () => (<svg className="rec-icon" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
  
	// Section-tab switch handler: switching tab clears & closes the right chat panel
	const handleTabSwitch = (newTab) => {
	  if (newTab === activeTab) return;
	  
	  mediaPlayingRef.current = false;
	  setIsMediaPlaying(false);
	  setSelectedSession(null);
	  setMobileChatOpen(false);
	  setMessages([]);
	  setReplyMessage('');
	  clearMedia();
	  setReplyError(null);
	  setReplySuccess(null);
	  setShowMediaOptions(false);
	  setVoiceEligibility(null);
	  setCompletedSuccess(null);
	  setCompletedError(null);
	  setHandleSuccess(null);
	  setHandleError(null);
	  setResetSuccess(null);
	  setResetError(null);
	  setRevertSuccess(null);
	  setRevertError(null);
	  if (messagesPollingRef.current) clearInterval(messagesPollingRef.current);
	  
	  setActiveTab(newTab);
	};

	const handleSessionSelect = (session) => {
	  // Reset media playing state when switching sessions
	  mediaPlayingRef.current = false;
	  setIsMediaPlaying(false);
	  
	  setSelectedSession(session);
	  setMobileChatOpen(true);
	  setMessages([]);
	  setReplyMessage('');
	  clearMedia();
	  setReplyError(null);
	  setReplySuccess(null);
	  setShowMediaOptions(false);
	  setVoiceEligibility(null);
	  fetchMessages(session.session_id);
	  
	  if (session.new_message_count > 0) {
		resetNewMessageCount(session.session_id);
	  }
	  
	  if (platform === 'whatsapp' && session.phone_number) {
		checkVoiceMessageEligibility(session.phone_number);
	  }
	  
	  setShouldAutoScrollOnLoad(true);
	};
  
  const shouldShowUseAIReplyButton = () => {
    if (!selectedSession) return false;
    return selectedSession.human_reply === 1;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };
  
  const renderFormattedText = (text) => {
    if (!text) return '';
    
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedText.split('\n');
    
    return lines.map((line, index) => {
      if (line.trim() === '') {
        return <div key={index} style={{ height: '1.4em' }}>&nbsp;</div>;
      }
      
      return (
        <div key={index} style={{ minHeight: '1.4em' }}>
          {line}
        </div>
      );
    });
  };

	const getMediaUrl = (filePath, mediaType, message) => {
	  console.log(`🔍 [${platform.toUpperCase()}] Getting media URL for ${mediaType}:`, {
		filePath,
		messageRole: message?.role,
		messageContent: message?.message,
		openingMessageId: message?.opening_message_id,
		messageType: message?.message_type,
		mediaUrl: message?.media_url,
		mediaFilename: message?.media_filename
	  });
	  
	  // Early return check
	  if (!filePath && !message?.message && !message?.media_url && !message?.media_filename) {
		console.log(`❌ [${platform.toUpperCase()}] No media source found - returning null`);
		return null;
	  }
	  
	  // ✅ PRIORITY 1: Opening messages from assistant (WhatsApp, Facebook, and 2nd Facebook Page)
	  if (message?.role === 'assistant' && message?.opening_message_id) {
		let filename;
		
		if (platform === 'facebook' || platform === 'facebook2') {
		  // Facebook stores opening message filename in media_filename column
		  filename = message.media_filename;
		} else {
		  // WhatsApp stores filename directly in message column
		  filename = message.message;
		}
		
		if (!filename) {
		  console.log(`⚠️ [${platform.toUpperCase()}] Opening message but no filename found`);
		  return null;
		}
		
		console.log(`✅ [${platform.toUpperCase()}] OPENING MESSAGE DETECTED!`, {
		  filename,
		  openingMessageId: message.opening_message_id,
		  generatedUrl: `/api/chatboxContent/media_opening/${filename}`
		});
		
		return `/api/chatboxContent/media_opening/${filename}`;
	  }
	  
	  // ✅ PRIORITY 2: Admin-sent messages from send_media folder
	  if (message?.role === 'admin') {
		console.log(`📤 [${platform.toUpperCase()}] Admin ${mediaType}:`, { filePath, mediaUrl: message?.media_url });
		
		// Admin-sent media is uploaded straight to Cloudinary (see uploadToCloudinary
		// in chatboxContent.js). Route it through the same Node proxy the
		// user-sent branch below uses (PRIORITY 3) — that proxy already knows how
		// to serve Cloudinary-backed paths, so admin and user media share one path.
		if (platform === 'facebook' || platform === 'facebook2') {
		  const mediaPath = message.media_url;
		  if (mediaPath) {
			// Skip if it's an opening message path
			if (mediaPath.startsWith('media_opening/')) {
			  console.log(`⚠️ [${platform.toUpperCase()}] Unexpected media_opening path for admin message`);
			  return null;
			}
			
			if (mediaPath.startsWith('http')) {
			  console.log(`🌐 [${platform.toUpperCase()}] External URL:`, mediaPath);
			  return mediaPath;
			}
			const proxyUrl = `/api/chatboxContent/${mediaType}s/${mediaPath}`;
			console.log(`🔄 [${platform.toUpperCase()}] Admin proxy URL:`, proxyUrl);
			return proxyUrl;
		  }
		} else {
		  // WhatsApp
		  if (filePath) {
			if (filePath.startsWith('http')) {
			  console.log(`🌐 [WHATSAPP] External admin URL:`, filePath);
			  return filePath;
			}
			const proxyUrl = `/api/chatboxContent/${mediaType}s/${filePath}`;
			console.log(`🔄 [WHATSAPP] Admin proxy URL:`, proxyUrl);
			return proxyUrl;
		  }
		}
	  }
	  
	  // ✅ PRIORITY 2.5: Bot-triggered special-reply media (role === 'assistant'
	  // WITHOUT opening_message_id — distinct from PRIORITY 1's opening messages).
	  // chatbot.py / fb_chatbot.py copy the special-reply library file into
	  // send_media when a keyword triggers it, storing that send_media path in
	  // the same columns as admin/user media — so this uses the same proxy
	  // pattern as PRIORITY 2 and PRIORITY 3.
	  if (message?.role === 'assistant' && !message?.opening_message_id) {
		console.log(`🤖 [${platform.toUpperCase()}] Bot special-reply ${mediaType}:`, { filePath, mediaUrl: message?.media_url });
		
		if (platform === 'facebook' || platform === 'facebook2') {
		  const mediaPath = message.media_url;
		  if (mediaPath) {
			if (mediaPath.startsWith('media_opening/')) {
			  console.log(`⚠️ [${platform.toUpperCase()}] Unexpected media_opening path for bot message`);
			  return null;
			}
			
			if (mediaPath.startsWith('http')) {
			  console.log(`🌐 [${platform.toUpperCase()}] External URL:`, mediaPath);
			  return mediaPath;
			}
			const proxyUrl = `/api/chatboxContent/${mediaType}s/${mediaPath}`;
			console.log(`🔄 [${platform.toUpperCase()}] Bot special-reply proxy URL:`, proxyUrl);
			return proxyUrl;
		  }
		} else {
		  // WhatsApp
		  if (filePath) {
			if (filePath.startsWith('http')) {
			  console.log(`🌐 [WHATSAPP] External bot URL:`, filePath);
			  return filePath;
			}
			const proxyUrl = `/api/chatboxContent/${mediaType}s/${filePath}`;
			console.log(`🔄 [WHATSAPP] Bot special-reply proxy URL:`, proxyUrl);
			return proxyUrl;
		  }
		}
	  }
	  
	  // ✅ PRIORITY 3: User-sent messages (from Python Railway service)
	  if (message?.role === 'user') {
		console.log(`👤 [${platform.toUpperCase()}] User ${mediaType}:`, { filePath, mediaUrl: message?.media_url });
		
		if (platform === 'facebook' || platform === 'facebook2') {
		  const mediaPath = message.media_url;
		  if (mediaPath) {
			// Skip if it's an opening message path
			if (mediaPath.startsWith('media_opening/')) {
			  console.log(`⚠️ [${platform.toUpperCase()}] Unexpected media_opening path for user message`);
			  return null;
			}
			
			if (mediaPath.startsWith('http')) {
			  console.log(`🌐 [${platform.toUpperCase()}] External URL:`, mediaPath);
			  return mediaPath;
			}
			const proxyUrl = `/api/chatboxContent/${mediaType}s/${mediaPath}`;
			console.log(`🔄 [${platform.toUpperCase()}] Proxy URL:`, proxyUrl);
			return proxyUrl;
		  }
		} else {
		  // WhatsApp
		  if (filePath) {
			const proxyUrl = `/api/chatboxContent/${mediaType}s/${filePath}`;
			console.log(`🔄 [WHATSAPP] Proxy URL:`, proxyUrl);
			return proxyUrl;
		  }
		}
	  }
	  
	  console.log(`❌ [${platform.toUpperCase()}] No URL could be generated`, {
		role: message?.role,
		hasOpeningId: !!message?.opening_message_id,
		hasFilePath: !!filePath,
		hasMessageContent: !!message?.message,
		hasMediaUrl: !!message?.media_url,
		hasMediaFilename: !!message?.media_filename
	  });
	  return null;
	};

	// Media viewer components - simplified versions
	const ImageViewer = ({ imageFilePath, imageFileName, mediaCaption, message }) => {
	  const [imageError, setImageError] = useState(false);
	  const [isLoading, setIsLoading] = useState(true);

	  const imageUrl = getMediaUrl(imageFilePath, 'image', message);

	  console.log('🖼️ ImageViewer rendering:', {
		imageUrl,
		imageFilePath,
		imageFileName,
		hasMessage: !!message,
		openingMessageId: message?.opening_message_id
	  });

	  if (!imageUrl) {
		console.log('❌ ImageViewer: No URL generated');
		return (
		  <div className="image-message">
			<span>🖼️ Image message (file not available)</span>
			{mediaCaption && <p className="media-caption">{mediaCaption}</p>}
		  </div>
		);
	  }

	  const displayFileName = imageFileName || (imageFilePath ? imageFilePath.split('/').pop() : null);

	  return (
		<div className="image-message">
		  {isLoading && <div className="media-loading">Loading image...</div>}
		  {imageError ? (
			<div className="media-error">
			  <span>🖼️ Image unavailable</span>
			  {displayFileName && <p className="file-name">{displayFileName}</p>}
			  <p style={{fontSize: '11px', color: '#999'}}>URL: {imageUrl}</p>
			</div>
		  ) : (
			<img
			  src={imageUrl}
			  alt={mediaCaption || displayFileName || "Shared image"}
			  className="shared-image"
			  onLoad={() => {
				console.log('✅ Image loaded successfully:', imageUrl);
				setIsLoading(false);
			  }}
			  onError={(e) => {
				console.error('❌ Image load failed:', {
				  url: imageUrl,
				  error: e,
				  naturalWidth: e.target?.naturalWidth,
				  naturalHeight: e.target?.naturalHeight
				});
				setImageError(true);
				setIsLoading(false);
			  }}
			  style={{ display: isLoading ? 'none' : 'block' }}
			/>
		  )}
		  {mediaCaption && <p className="media-caption">{mediaCaption}</p>}
		</div>
	  );
	};

	// VideoPlayer is now defined at module level above ChatboxPage (see top of file).
	
  // AudioPlayer is now defined at module level above ChatboxPage (see top of file).

  const DocumentViewer = ({ documentFilePath, documentFileName, documentExtractedText, mediaCaption, message }) => {
    const documentUrl = getMediaUrl(documentFilePath, 'document', message);
    
    const getFileName = (filePath, dbFileName) => {
      if (dbFileName) return dbFileName;
      if (!filePath) return 'Unknown file';
      const normalizedPath = filePath.replace(/\\/g, '/');
      return normalizedPath.split('/').pop() || 'Unknown file';
    };

    const fileName = getFileName(documentFilePath, documentFileName);
    
    const getFileIcon = (fileName) => {
      const extension = fileName.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'pdf': return '📄';
        case 'doc':
        case 'docx': return '📝';
        case 'xls':
        case 'xlsx': return '📊';
        case 'txt': return '📃';
        default: return '📄';
      }
    };

    if (!documentUrl) {
      return (
        <div className="document-message">
          <span>📄 Document message (file not available)</span>
          {mediaCaption && <p className="media-caption">{mediaCaption}</p>}
        </div>
      );
    }

    return (
      <div className="document-message">
        <div className="document-info">
          <span className="document-icon">{getFileIcon(fileName)}</span>
          <div className="document-details">
            <span className="document-name">{fileName}</span>
            <button 
              className="download-btn"
              onClick={() => {
                const link = document.createElement('a');
                link.href = documentUrl;
                link.download = fileName;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              💾 Download
            </button>
          </div>
        </div>
        {mediaCaption && <p className="media-caption">{mediaCaption}</p>}
      </div>
    );
  };

	const renderEnhancedChatHeader = () => {
		if (!selectedSession) return null;
		
		// For WhatsApp, format phone number
		// For Facebook / 2nd Facebook Page, show user name if available, otherwise show user_id
		let displayName;
		
		if (platform === 'whatsapp') {
		  displayName = formatPhoneNumber(selectedSession.phone_number, selectedSession.client_name);
		} else if (platform === 'facebook' || platform === 'facebook2') {
		  // Facebook: show user name with smaller user_id
		  if (facebookUserName) {
			displayName = (
			  <span>
				{facebookUserName} <span style={{ fontSize: '0.75em', color: '#EDEDED' }}>({selectedSession.user_id})</span>
			  </span>
			);
		  } else if (loadingFacebookName) {
			displayName = <span>Loading... <span style={{ fontSize: '0.75em', color: '#EDEDED' }}>({selectedSession.user_id})</span></span>;
		  } else {
			displayName = selectedSession.user_id;
		  }
		}
		
		const isRegisteredClient = platform === 'whatsapp' && selectedSession.client_name && selectedSession.client_name.trim();

		// Inline handled badge (no 首次处理)
		const handledBadge = platform === 'whatsapp' && selectedSession.mark_handled === 1 ? (() => {
		  const fmtHandled = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '';
		  const lastBy = selectedSession.mark_handled_last_by_name || selectedSession.mark_handled_last_by;
		  const lastAt = selectedSession.mark_handled_last_at;
		  return (
			<span className="header-status-badge header-handled-badge">
			  📌 Handled{lastBy ? ` · ${lastBy}` : ''}{lastAt ? ` (${fmtHandled(lastAt)})` : ''}
			</span>
		  );
		})() : null;

		// Inline completed badge (no 首次完成)
		const completedBadge = (platform === 'whatsapp' || platform === 'facebook2') && selectedSession.appointment_completed === 1 ? (() => {
		  const fmtCompleted = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '';
		  const lastBy = selectedSession.appointment_completed_last_by_name || selectedSession.appointment_completed_last_by;
		  const lastAt = selectedSession.appointment_completed_last_at;
		  return (
			<span className="header-status-badge header-completed-badge">
			  ✅ Completed{lastBy ? ` · ${lastBy}` : ''}{lastAt ? ` (${fmtCompleted(lastAt)})` : ''}
			</span>
		  );
		})() : null;
		
		return (
		  <div className={`chat-header ${platform}`}>
			<button
			  type="button"
			  className="mobile-chat-back-btn"
			  onClick={() => setMobileChatOpen(false)}
			  aria-label="Back to chat sessions"
			>
			  ← Sessions
			</button>
			<div className="chat-header-info">
			  {/* Title row: "Chat with [ClientBadge] [Name] [HandledBadge] [CompletedBadge]" */}
			  <div className="chat-header-title-row">
				<h3>
				  Chat with{' '}
				  {isRegisteredClient && (
					<span className="header-display-name">{selectedSession.client_name}</span>
				  )}
				  {' '}
				  {displayName}
				</h3>
				<div className="header-inline-badges">
				  {handledBadge}
				  {completedBadge}
				</div>
			  </div>

			  {/* 3 Action Buttons row — WhatsApp only */}
			  {platform === 'whatsapp' && (
				<div className="header-action-buttons">
				  <button
					className="hdr-action-btn hdr-chat-wa-btn"
					title={user ? 'Open chat using another WhatsApp account' : 'Login required'}
					onClick={handleOpenWhatsApp}
					disabled={!user}
				  >
					<span className="hdr-btn-main">Chat</span>
					<span className="hdr-btn-sub">using other WhatsApp</span>
				  </button>
				  <button
					className="hdr-action-btn hdr-save-client-btn"
					title={user ? (isRegisteredClient ? 'Edit client info' : 'Save this contact as a client') : 'Login required'}
					onClick={handleSaveClientOpen}
					disabled={!user}
				  >
					<span className="hdr-btn-main">{isRegisteredClient ? 'Edit Client' : 'Save Client'}</span>
				  </button>
				  <button
					className="hdr-action-btn hdr-add-appt-btn"
					title={isRegisteredClient ? 'Add a new appointment' : 'Save contact as client first'}
					onClick={handleAddAppointmentClick}
				  >
					<span className="hdr-btn-main">Add Appointment</span>
				  </button>
				</div>
			  )}

			  {isMediaPlaying && (
				<div className="media-playing-indicator">
				  <span className="playing-dot"></span>
				  Media Playing - Updates Paused
				</div>
			  )}
			</div>
			
			<div className="chat-header-actions">
			  {/* Use AI Reply Button */}
			  {shouldShowUseAIReplyButton() && (
				<button
				  className="reset-flags-btn"
				  onClick={resetSessionFlags}
				  disabled={resettingFlags}
				  title="Return control to AI Reply"
				>
				  {resettingFlags ? (
					<>
					  <span className="loading-spinner"></span>
					  Processing...
					</>
				  ) : (
					<>
					  Use AI Reply
					</>
				  )}
				</button>
			  )}

			  {/* Mark / Cancel Handle Button for Aida & Human Service Sessions (WhatsApp only) */}
			  {platform === 'whatsapp' && selectedSession?.appointment_section !== 1 && (
				selectedSession?.mark_handled === 1 ? (
				  <button
					className="cancel-handle-btn"
					onClick={() => toggleHandle(0)}
					disabled={markingHandle}
					title="Cancel handled status for this session"
				  >
					{markingHandle ? (
					  <><span className="loading-spinner"></span>Processing...</>
					) : (
					  '↩ Cancel Handle'
					)}
				  </button>
				) : (
				  <button
					className="handle-btn"
					onClick={() => toggleHandle(1)}
					disabled={markingHandle}
					title="Mark this session as handled"
				  >
					{markingHandle ? (
					  <><span className="loading-spinner"></span>Processing...</>
					) : (
					  '📌 Mark Handle'
					)}
				  </button>
				)
			  )}
			
			  {/* Mark Completed / Incomplete Button for Appointment Sessions */}
			  {(platform === 'whatsapp' || platform === 'facebook2') && selectedSession?.appointment_section === 1 && (
				selectedSession?.appointment_completed === 1 ? (
				  <button
					className="appointment-incomplete-btn"
					onClick={() => toggleAppointmentCompleted(0)}
					disabled={markingCompleted}
					title="Mark this appointment as incomplete"
				  >
					{markingCompleted ? (
					  <><span className="loading-spinner"></span>Processing...</>
					) : (
					  '↩ Incomplete'
					)}
				  </button>
				) : (
				  <button
					className="appointment-completed-btn"
					onClick={() => toggleAppointmentCompleted(1)}
					disabled={markingCompleted}
					title="Mark this appointment as completed"
				  >
					{markingCompleted ? (
					  <><span className="loading-spinner"></span>Processing...</>
					) : (
					  '✔ Mark Completed'
					)}
				  </button>
				)
			  )}
			</div>
		  </div>
		);
	  };

  const getEnhancedReplyPlaceholder = () => {
    if (!selectedSession) return "Type a message...";
    
    let displayName;
    if (platform === 'whatsapp') {
      displayName = formatPhoneNumber(selectedSession.phone_number, selectedSession.client_name);
    } else if (platform === 'facebook' || platform === 'facebook2') {
      // For Facebook / 2nd Facebook Page, use user name if available
      displayName = facebookUserName || selectedSession.user_id;
    }
    
    return `Type a message to ${displayName}...`;
  };

  if (!user) return null;

  if (loading) {
    return (
      <Layout>
        <div className="loading">
          <div>Loading chat sessions...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error">
          <div>Error: {error}</div>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '10px', padding: '5px 10px' }}
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <>
    <Layout>
      <div className="chatbox-container">
        {/* Platform Selector */}
        <div className="platform-selector">
		  <span className="platform-selector-label">Select Platform:</span>
		  
		  <button
			className={`platform-btn ${platform === 'whatsapp' ? 'active whatsapp' : ''}`}
			onClick={() => handlePlatformSwitch('whatsapp')}
		  >
			<span className="platform-btn-text">WhatsApp</span>
			{getTotalNewMessages('whatsapp') > 0 && (
			  <span className="platform-new-badge whatsapp-badge">
				{getTotalNewMessages('whatsapp')}
			  </span>
			)}
		  </button>
		  
		  <button
			className={`platform-btn ${platform === 'facebook' ? 'active facebook' : ''}`}
			onClick={() => handlePlatformSwitch('facebook')}
		  >
			<span className="platform-btn-text">FB Ikigai ForHim</span>
			{getTotalNewMessages('facebook') > 0 && (
			  <span className="platform-new-badge facebook-badge">
				{getTotalNewMessages('facebook')}
			  </span>
			)}
		  </button>
		  
		  <button
			className={`platform-btn ${platform === 'facebook2' ? 'active facebook2' : ''}`}
			onClick={() => handlePlatformSwitch('facebook2')}
		  >
			<span className="platform-btn-text">FB Brandfolks ForHim</span>
			{getTotalNewMessages('facebook2') > 0 && (
			  <span className="platform-new-badge facebook2-badge">
				{getTotalNewMessages('facebook2')}
			  </span>
			)}
		  </button>
		</div>

        <div className={`chatbox-main ${mobileChatOpen ? 'mobile-chat-open' : ''}`}>
          {/* Sessions List */}
          <div className={`sessions-sidebar ${platform}`}>
            {/* Filter Tabs - Aida, Appointment, Human Service for all platforms */}
            <div className="filter-tabs">
              <button
                className={`tab-button ${activeTab === 'aida' ? 'active' : ''}`}
                onClick={() => handleTabSwitch('aida')}
              >
                ({getSessionCounts().aida}) Aida 
              </button>
              <button
                className={`tab-button ${activeTab === 'appointment' ? 'active' : ''}`}
                onClick={() => handleTabSwitch('appointment')}
              >
                ({getSessionCounts().appointment}) Appointment 
              </button>
              <button
                className={`tab-button ${activeTab === 'human' ? 'active' : ''}`}
                onClick={() => handleTabSwitch('human')}
              >
                ({getSessionCounts().human}) Human Service 
              </button>
            </div>

            <h3>
              <>
                {activeTab === 'aida' && 'Aida Chat Sessions'}
                {activeTab === 'appointment' && 'Appointment Sessions'}
                {activeTab === 'human' && 'Human Service Sessions'}
                ({getFilteredSessions().length})
              </>
            </h3>
            
            <div className="sessions-list">
              {getFilteredSessions().length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  {activeTab === 'aida' && 'No Aida chat sessions found'}
                  {activeTab === 'appointment' && 'No appointment sessions found'}
                  {activeTab === 'human' && 'No human service sessions found'}
                </div>
              ) : (
                getFilteredSessions().map((session) => renderSessionItem(session))
              )}
            </div>
          </div>

          {/* Messages Display */}
          <div className="messages-container">
            {selectedSession ? (
              <>
                {renderEnhancedChatHeader()}
                
                <div className="messages-list" ref={messagesListRef}>
				  {loadingMessages ? (
					<div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
					  Loading messages...
					</div>
				  ) : messages.length === 0 ? (
					<div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
					  No messages found for this session
					</div>
				  ) : (
					messages.map((message) => (
					  <div
						key={`${message.message_id || message.PKKEY}_${message.timestamp || message.created_at}`}
						className={`message ${message.role === 'user' || message.message_type === 'user' ? 'assistant-message' : 'user-message'}`}
					  >
						<div className={`message-content ${platform} ${message.role === 'user' || message.message_type === 'user' ? 'assistant-message' : 'user-message'}`}>
						  {message.message_type === 'text' && (
							<div style={{
							  wordWrap: 'break-word',
							  lineHeight: 1.4,
							  fontSize: '15px',
							  fontFamily: 'inherit'
							}}>
							  {renderFormattedText(message.message || message.message_text)}
							</div>
						  )}
						  {message.message_type === 'image' && (
							<ImageViewer 
							  imageFilePath={(platform === 'facebook' || platform === 'facebook2') ? message.media_url : message.image_file_path}
							  imageFileName={(platform === 'facebook' || platform === 'facebook2') ? message.media_filename : message.image_file_name}
							  mediaCaption={message.media_caption}
							  message={message}
							/>
						  )}
						  {message.message_type === 'video' && (
							<VideoPlayer
							  videoUrl={getMediaUrl(
							    (platform === 'facebook' || platform === 'facebook2') ? message.media_url : message.video_file_path,
							    'video',
							    message
							  )}
							  videoFileName={(platform === 'facebook' || platform === 'facebook2') ? message.media_filename : message.video_file_name}
							  mediaCaption={message.media_caption}
							  mediaPlayingRef={mediaPlayingRef}
							  setIsMediaPlaying={setIsMediaPlaying}
							/>
						  )}
						  {message.message_type === 'audio' && (
							<AudioPlayer
							  audioUrl={
							    // Opening messages: audio_file_path is null; PRIORITY 1 in getMediaUrl
							    // uses message.message (the MEDIANAME) to build the proxy URL.
							    // Non-opening messages: use audio_file_path as usual.
							    getMediaUrl(
							      (platform === 'facebook' || platform === 'facebook2')
							        ? message.media_url
							        : message.audio_file_path,
							      'audio',
							      message
							    )
							  }
							  audioFileName={
							    // For opening audio, audio_file_name is null because the message was
							    // saved via save_message (not save_message_with_media).
							    // Fall back to message.message which holds the raw MEDIANAME filename.
							    (platform === 'facebook' || platform === 'facebook2')
							      ? message.media_filename
							      : (message.audio_file_name ||
							          (message.opening_message_id ? message.message : null))
							  }
							  transcribedText={message.transcribed_text}
							  mediaCaption={message.media_caption}
							  mediaPlayingRef={mediaPlayingRef}
							  setIsMediaPlaying={setIsMediaPlaying}
							/>
						  )}
						  {message.message_type === 'document' && (
							<DocumentViewer 
							  documentFilePath={(platform === 'facebook' || platform === 'facebook2') ? message.media_url : message.document_file_path}
							  documentFileName={(platform === 'facebook' || platform === 'facebook2') ? message.media_filename : message.document_file_name}
							  documentExtractedText={message.document_extracted_text}
							  mediaCaption={message.media_caption}
							  message={message}
							/>
						  )}
						</div>
						<div className="message-meta">
						  <span className="timestamp">{formatDate(message.timestamp || message.created_at)}</span>
						  <span className="role">{message.role || message.message_type}</span>
						</div>
					  </div>
					))
				  )}
				</div>

                {/* Reply Section */}
                <div className="reply-section">
                  <MediaPreview />
                  
                  {platform === 'whatsapp' && (isRecording || recordedBlob) && (
                    <div className="recorder-bar">
                      <div className={`rec-indicator ${isPausedRecording ? 'paused' : 'live'}`}></div>
                      <div className="rec-time">{fmtTime(recordingSeconds)}</div>
                      <div className="rec-wave-wrap">
                        <canvas ref={waveformCanvasRef} className="rec-waveform"></canvas>
                        {recordedUrl && !isRecording && (
                          <audio controls src={recordedUrl} className="rec-audio-preview"></audio>
                        )}
                      </div>
                      <div className="rec-controls">
                        <button className="rec-btn danger" onClick={cancelRecording} title={isRecording ? 'Cancel' : 'Delete'}>
                          <TrashIcon/>
                        </button>
                        {isRecording && (
                          <>
                            {isPausedRecording ? (
                              <button className="rec-btn" onClick={resumeRecording} title="Resume"><ResumeIcon/></button>
                            ) : (
                              <button className="rec-btn" onClick={pauseRecording} title="Pause"><PauseIcon/></button>
                            )}
                            <button className="rec-btn" onClick={stopRecording} title="Finish"><StopIcon/></button>
                          </>
                        )}
                        {!isRecording && recordedBlob && (
                          <button className="rec-btn primary" onClick={sendRecordedAudio} title="Send"><SendIcon/></button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className={`reply-input-container ${platform}`}>
                    <MediaOptions />
                    
                    <button
                      className={`media-attach-btn ${showMediaOptions ? 'active' : ''}`}
                      onClick={() => setShowMediaOptions(!showMediaOptions)}
                      disabled={sendingReply || uploadingMedia}
                      title="Attach media"
                    >
                      📎
                    </button>
                    
                    {platform === 'whatsapp' && (
                      <button
                        className={`mic-btn ${isRecording ? 'active' : ''} ${!voiceEligibility?.can_send_voice ? 'disabled' : ''}`}
                        onClick={() => { 
                          if (!isRecording && voiceEligibility?.can_send_voice) {
                            startRecording();
                          } else if (!voiceEligibility?.can_send_voice) {
                            setReplyError(voiceEligibility?.message || 'Voice messages not available');
                            setTimeout(() => setReplyError(null), 5000);
                          }
                        }}
                        disabled={sendingReply || uploadingMedia || isRecording || !voiceEligibility?.can_send_voice}
                        title={voiceEligibility?.can_send_voice ? 
                          "Record voice message" : 
                          (voiceEligibility?.message || "Voice messages unavailable")
                        }
                      >
                        <MicIcon />
                      </button>
                    )}

                    <textarea
                      className="reply-input"
                      placeholder={selectedMedia ? "Add a caption..." : getEnhancedReplyPlaceholder()}
                      value={replyMessage}
                      onChange={handleReplyInputChange}
                      onKeyPress={handleReplyKeyPress}
                      disabled={sendingReply || uploadingMedia}
                      rows="1"
                      maxLength="1000"
                      style={{
                        resize: 'none',
                        overflow: 'hidden',
                        minHeight: '20px',
                        maxHeight: '120px'
                      }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                      }}
                    />
                    <button
                      className={`send-reply-btn ${platform}`}
                      onClick={sendMessage}
                      disabled={(!replyMessage.trim() && !selectedMedia) || sendingReply || uploadingMedia}
                      title="Send message"
                    >
                      {(sendingReply || uploadingMedia) ? (
                        <span className="loading-spinner"></span>
                      ) : (
                        <SendIcon />
                      )}
                    </button>
                  </div>
                  
                  <div className="character-count">
                    {replyMessage.length}/1000 characters
                    {selectedMedia && (
                      <span className="media-indicator">
                        • {mediaType} attached ({(selectedMedia.size / 1024).toFixed(1)} KB)
                      </span>
                    )}
                    {platform === 'whatsapp' && selectedSession && voiceEligibility && (
                      <span className={`voice-status ${voiceEligibility.can_send_voice ? 'available' : 'unavailable'}`}>
                        • Voice messages: {voiceEligibility.can_send_voice ? 'Available' : 'Unavailable'}
                      </span>
                    )}
                  </div>

                  {/* Hidden file inputs */}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'image')}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileChange(e, 'video')}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={documentInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                    onChange={(e) => handleFileChange(e, 'document')}
                    style={{ display: 'none' }}
                  />

                  {/* Status Messages */}
                  {replySuccess && (
                    <div className="reply-status success">
                      ✅ {replySuccess}
                    </div>
                  )}
                  
                  {replyError && (
                    <div className="reply-status error">
                      ❌ {replyError}
                    </div>
                  )}

                  {resetSuccess && (
                    <div className="reset-status success" style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      color: '#155724',
                      fontSize: '14px'
                    }}>
                      ✅ {resetSuccess}
                    </div>
                  )}

                  {resetError && (
                    <div className="reset-status error" style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      color: '#721c24',
                      fontSize: '14px'
                    }}>
                      ❌ {resetError}
                    </div>
                  )}

                  {handleSuccess && (
                    <div className="handle-status success" style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      color: '#155724',
                      fontSize: '14px'
                    }}>
                      ✅ {handleSuccess}
                    </div>
                  )}

                  {handleError && (
                    <div className="handle-status error" style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      color: '#721c24',
                      fontSize: '14px'
                    }}>
                      ❌ {handleError}
                    </div>
                  )}

                  {revertSuccess && (
                    <div className="revert-status success" style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      color: '#155724',
                      fontSize: '14px'
                    }}>
                      ✅ {revertSuccess}
                    </div>
                  )}

                  {revertError && (
                    <div className="revert-status error" style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      color: '#721c24',
                      fontSize: '14px'
                    }}>
                      ❌ {revertError}
                    </div>
                  )}

                  {completedSuccess && (
                    <div style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      color: '#155724',
                      fontSize: '14px'
                    }}>
                      ✅ {completedSuccess}
                    </div>
                  )}

                  {completedError && (
                    <div style={{
                      padding: '10px 15px',
                      margin: '10px 0',
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      color: '#721c24',
                      fontSize: '14px'
                    }}>
                      ❌ {completedError}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>Select a chat session to view messages and send replies</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>

    {/* ── Save / Edit Client Modal ── */}
    {showSaveClientModal && (
      <div className="sc-modal-overlay" onClick={() => setShowSaveClientModal(false)}>
        <div className="sc-modal" onClick={e => e.stopPropagation()}>
          <div className="sc-modal-header">
            <h3>{saveClientMode === 'edit' ? 'Edit Client' : 'Save Client'}</h3>
            <button className="sc-modal-close" onClick={() => setShowSaveClientModal(false)}>✕</button>
          </div>

          <div className="sc-modal-body">
            {loadingClientData ? (
              <div className="sc-loading">Loading client data...</div>
            ) : (
              <>
                <div className="sc-field">
                  <label>Name <span className="sc-required">*</span></label>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={saveClientForm.name}
                    onChange={e => setSaveClientForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="sc-field">
                  <label>Phone Number <span className="sc-required">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. 60123456789"
                    value={saveClientForm.phone}
                    onChange={e => setSaveClientForm(f => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="sc-field">
                  <label>IC <span className="sc-optional">(optional)</span></label>
                  <input
                    type="text"
                    placeholder="e.g. 901231-01-1234"
                    value={saveClientForm.ic}
                    onChange={e => setSaveClientForm(f => ({ ...f, ic: e.target.value }))}
                  />
                </div>
                <div className="sc-field">
                  <label>Date of Birth <span className="sc-optional">(optional)</span></label>
                  <input
                    type="date"
                    value={saveClientForm.dob}
                    onChange={e => setSaveClientForm(f => ({ ...f, dob: e.target.value }))}
                  />
                </div>
                <div className="sc-field">
                  <label>Email <span className="sc-optional">(optional)</span></label>
                  <input
                    type="email"
                    placeholder="e.g. name@email.com"
                    value={saveClientForm.email}
                    onChange={e => setSaveClientForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>
                {saveClientError && <div className="sc-error">❌ {saveClientError}</div>}
                {saveClientSuccess && <div className="sc-success">✅ {saveClientSuccess}</div>}
              </>
            )}
          </div>

          <div className="sc-modal-footer">
            <div className="appt-footer-btns">
              <button className="sc-btn-cancel" onClick={() => setShowSaveClientModal(false)} disabled={savingClient || loadingClientData}>
                Cancel
              </button>
              <button className="sc-btn-save" onClick={handleSaveClientSubmit} disabled={savingClient || loadingClientData}>
                {savingClient ? 'Saving...' : saveClientMode === 'edit' ? 'Update Client' : 'Save Client'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ── Add Appointment Modal ── */}
    {showAddApptModal && (() => {
      const apptDurationOptions = [
        { value: 15, label: '15 minutes' }, { value: 30, label: '30 minutes' },
        { value: 45, label: '45 minutes' }, { value: 60, label: '1 hour' },
        { value: 90, label: '1.5 hours' }, { value: 120, label: '2 hours' }
      ];
      return (
        <div className="sc-modal-overlay" onClick={() => { setShowAddApptModal(false); apptResetModal(); }}>
          <div className="sc-modal appt-modal" onClick={e => e.stopPropagation()}>
            <div className="sc-modal-header">
              <h3>Add Appointment</h3>
              <button className="sc-modal-close" onClick={() => { setShowAddApptModal(false); apptResetModal(); }}>✕</button>
            </div>
            <div className="sc-modal-body">
              {/* Locked Client (read-only) */}
              <div className="sc-field">
                <label>👤 Customer</label>
                <div className="appt-locked-client">
                  <strong>{apptLockedClient?.name}</strong>
                  {apptLockedClient?.ic && <span className="appt-locked-ic">IC: {apptLockedClient.ic}</span>}
                  <span className="appt-locked-badge">🔒 Auto-selected</span>
                </div>
              </div>

              {/* Title */}
              <div className="sc-field">
                <label>Appointment Title <span className="sc-required">*</span></label>
                <input
                  type="text"
                  placeholder="Enter appointment title"
                  value={apptForm.title}
                  onChange={e => setApptForm(f => ({ ...f, title: e.target.value }))}
                />
              </div>

              {/* Date */}
              <div className="sc-field">
                <label>Date <span className="sc-required">*</span></label>
                <input
                  type="date"
                  value={apptForm.appointment_date}
                  onChange={e => apptHandleDateChange(e.target.value)}
                />
              </div>

              {/* Branch */}
              <div className="sc-field">
                <label>📍 Branch <span className="sc-required">*</span></label>
                <select value={apptForm.branch_id} onChange={e => apptHandleBranchChange(e.target.value)}>
                  <option value="">Select branch...</option>
                  {apptBranches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              {/* Consultant */}
              <div className="sc-field">
                <label>🩺 Consultant <span className="sc-required">*</span></label>
                <select
                  value={apptForm.doctor_id}
                  onChange={e => apptHandleDoctorChange(e.target.value)}
                  disabled={!apptForm.branch_id}
                >
                  <option value="">{!apptForm.branch_id ? 'Select branch first' : 'Select consultant...'}</option>
                  {apptDoctorsByBranch.map(d => (
                    <option key={d.id} value={d.id}>{d.name}{d.position ? ` — ${d.position}` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Start Time */}
              <div className="sc-field">
                <label>Start Time <span className="sc-required">*</span></label>
                {apptIsRestDay ? (
                  <div className="appt-rest-day-notice">🚫 Consultant is on rest day — no slots available</div>
                ) : (
                  <>
                    <select
                      value={apptForm.start_time}
                      onChange={e => {
                        const val = e.target.value;
                        setApptForm(prev => ({ ...prev, start_time: val }));
                        apptValidateTimeSlot(val, apptForm.duration_minutes, apptBookedSlots, apptIsRestDay);
                      }}
                      disabled={!apptForm.doctor_id || apptAvailableSlots.length === 0}
                    >
                      <option value="">
                        {!apptForm.doctor_id ? 'Select consultant first' :
                          apptAvailableSlots.length === 0 ? 'No available slots' : 'Select time'}
                      </option>
                      {apptAvailableSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                    {apptWorkingHours && (
                      <small className="appt-working-hours">🕐 Working hours: {apptWorkingHours}</small>
                    )}
                    {apptTimeConflict && (
                      <div className="appt-conflict-notice">{apptTimeConflict}</div>
                    )}
                  </>
                )}
              </div>

              {/* Duration */}
              <div className="sc-field">
                <label>Duration</label>
                <select
                  value={apptForm.duration_minutes}
                  onChange={e => {
                    const val = parseInt(e.target.value);
                    setApptForm(prev => ({ ...prev, duration_minutes: val }));
                    if (apptForm.start_time) apptValidateTimeSlot(apptForm.start_time, val, apptBookedSlots, apptIsRestDay);
                  }}
                >
                  {apptDurationOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="sc-field">
                <label>Description</label>
                <textarea
                  placeholder="Enter appointment description"
                  rows="3"
                  value={apptForm.description}
                  onChange={e => setApptForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
            </div>

            <div className="sc-modal-footer">
              {apptAlertMessage && (
                <div className="appt-footer-alert">
                  <span>⚠️ {apptAlertMessage}</span>
                  <button onClick={() => setApptAlertMessage('')}>✕</button>
                </div>
              )}
              <div className="appt-footer-btns">
                <button className="sc-btn-cancel" onClick={() => { setShowAddApptModal(false); apptResetModal(); }} disabled={apptLoading}>
                  Cancel
                </button>
                <button className="sc-btn-save" onClick={handleSaveAppt} disabled={apptLoading}>
                  {apptLoading ? 'Saving...' : 'Save Appointment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    })()}
    </>
  );
};

export default ChatboxPage;